// i18n — Spanish strings (parallel to en.ts).
//
// Every key in en.ts MUST exist here with the same structure. The
// TypeScript type enforces this at compile time (see index.ts).

import type { Translations } from './en';

const es: Translations = {
  // ── Producto ───────────────────────────────────────────────────────
  product: {
    name: 'Venuz',
    tagline: 'IDE de IA para founders',
  },

  // ── Verbos de estado (Layer 7 D70) ─────────────────────────────────
  status: {
    running: 'Trabajando',
    waiting: 'Esperando',
    finished: 'Listo',
    error: 'Falló',
    interrupted: 'Parado',
    stuck: 'Bloqueado',
    archived: 'Archivado',
    // aria-label atom-level para `StatusIcon`. Tras sprint
    // `sidebar-status-icons-binary` (2026-05-12) es la única copy de
    // "inactivo" que sobrevive — el badge a nivel sidebar
    // (`agentList.idle` = 'Inactivo · Envía un mensaje') se eliminó
    // cuando `CircleCheck` asumió la señal de "listo para input".
    idle: 'Inactivo',
  },

  // ── Topbar ─────────────────────────────────────────────────────────
  topbar: {
    openMenu: 'Abrir menú',
    closeMenu: 'Cerrar menú',
    signOut: 'Cerrar sesión',
    notifications: 'Notificaciones',
    noNotifications: 'Sin notificaciones',
    unreadAgents: '{count} agentes sin leer',
    settings: 'Ajustes',
    accountMenu: 'Menú de cuenta',
    theme: 'Tema',
    // Sprint S20.5 PR-3 (D013-EC-69) — banner persistente de runners
    // offline. Sin ICU en el i18n: claves explícitas singular/plural.
    runnerBanner: {
      unavailableOne: '1 servidor offline. Los nuevos agentes se asignarán al resto.',
      unavailableMany: '{count} servidores offline. Los nuevos agentes se asignarán al resto.',
      allDown: 'Todos los servidores offline. No se pueden crear agentes nuevos ahora mismo.',
      goToServers: 'Abrir Servidores',
    },
  },

  // ── Estado de conexión ─────────────────────────────────────────────
  connection: {
    connected: 'Conectado',
    reconnecting: 'Reconectando',
    disconnected: 'Desconectado — toca para reintentar',
  },

  // Sprint runner-picker-on-create — modal al pulsar "+" para que el
  // founder elija a qué runner va el nuevo agente, viendo capacidad
  // restante en el momento de elegir.
  runnerPicker: {
    title: 'Elige un runner',
    subtitle: 'El nuevo agente se crea en el runner que elijas.',
    autoLabel: 'Auto (recomendado)',
    autoHelper: 'Deja que la oficina use el runner con más capacidad libre.',
    capacityFree: '{percent}% libre',
    capacityUnknown: 'Sin datos de capacidad',
    // Sprint runner-picker-session-primary — línea secundaria bajo el
    // "% libre" grande de session 5h. Se compone uniendo los buckets
    // con " · ". Misma unidad ("libre") que la primaria.
    bucketWeekly: 'Weekly {percent}% libre',
    bucketSonnet: 'Sonnet {percent}% libre',
    // Sprint usage-limits-v2 (2026-07-08) — límites scoped por modelo
    // desde el array canónico `limits[]`.
    bucketWeeklyScoped: 'Weekly · scoped {percent}% libre',
    bucketWeeklyScopedModel: 'Weekly · {model} {percent}% libre',
    offlineReason: 'Offline — elige otro runner.',
    drainingReason: 'En drenado — no acepta nuevos agentes.',
    confirm: 'Crear agente',
    cancel: 'Cancelar',
  },

  // ── Sidebar ────────────────────────────────────────────────────────
  sidebar: {
    newAgent: 'Nuevo Agente',
    newAgentDisabled: 'Agentes nuevos pausados — servidor bajo en memoria',
    search: 'Buscar agentes',
    searchShortcutAriaLabel: 'Foco en búsqueda (Cmd+K)',
    resizeAriaLabel: 'Redimensionar barra lateral',
    empty: {
      title: 'Aún no hay agentes',
      subtitle: 'Crea el primero',
    },
    showMore: 'Mostrar {count} más',
    // Sprint agent-stages — las secciones por recencia (FIJADOS/HOY/AYER/
    // ANTERIORES) se retiraron; las secciones ahora son los estados de
    // workflow (grupo `stages` más abajo). ARCHIVADOS se mantiene.
    section: {
      archived: 'ARCHIVADOS',
    },
    project: {
      label: 'Proyecto',
      all: 'Todos los proyectos',
    },
  },

  // ── Estados de workflow (sprint agent-stages) ───────────────────────
  stages: {
    noStage: 'Sin estado',
    moveTo: 'Mover a estado',
    newStage: 'Nuevo estado…',
    manage: 'Gestionar estados…',
    manageTitle: 'Estados',
    createTitle: 'Nuevo estado',
    editTitle: 'Editar estado',
    newStageButton: 'Nuevo estado',
    emptyList: 'Aún no hay estados. Crea el primero.',
    nameLabel: 'Nombre',
    namePlaceholder: 'p. ej. Soak',
    colorLabel: 'Color',
    colorOption: 'Color {n}',
    create: 'Crear',
    save: 'Guardar',
    saving: 'Guardando…',
    cancel: 'Cancelar',
    rename: 'Editar',
    delete: 'Eliminar',
    moveUp: 'Subir',
    moveDown: 'Bajar',
    filterAll: 'Todos',
    deleteConfirmTitle: '¿Eliminar estado?',
    deleteConfirmBody:
      'Los {count} agentes de "{name}" pasarán a "Sin estado". Ningún agente se elimina.',
    createError: 'No se pudo crear el estado',
    updateError: 'No se pudo actualizar el estado',
    deleteError: 'No se pudo eliminar el estado',
  },

  // ── Columna de workspaces ───────────────────────────────────────────
  workspace: {
    addProject: 'Nuevo proyecto',
    newProject: 'Nuevo proyecto',
    repoUrl: 'URL del repo',
    slugInvalid: 'El slug debe empezar con letra o número y contener solo minúsculas, números, puntos, guiones.',
    cloning: 'Clonando…',
    creating: 'Creando…',
    create: 'Crear',
    done: 'Listo',
    namePlaceholder: 'Mi Proyecto',
    slugPlaceholder: 'mi-proyecto',
    repoPlaceholder: 'git@github.com:org/repo.git',
    branchPlaceholder: 'main',
  },

  // ── Personalización de workspace (Sprint 11b.2) ─────────────────────
  workspaceCustomization: {
    edit: 'Editar proyecto…',
    archive: 'Archivar',
    editTitle: 'Editar proyecto',
    initials: 'Iniciales',
    initialsHint: 'Exactamente 2 letras o números, en mayúsculas.',
    color: 'Color',
    autoColor: 'Color automático',
    autoGlyph: 'A',
    colorOption: 'Color {n}',
    resetAuto: 'Reiniciar a automático',
    save: 'Guardar',
    saving: 'Guardando…',
    reorderError: 'No pude reordenar — intenta de nuevo.',
    updateError: 'No pude actualizar — intenta de nuevo.',
    syncIncomplete: 'Sincronización incompleta en {count} servidor(es).',
  },

  // ── Menú contextual de agente ──────────────────────────────────────
  contextMenu: {
    markRead: 'Marcar leído',
    markUnread: 'Marcar no leído',
    rename: 'Renombrar',
    fork: 'Bifurcar',
    archive: 'Archivar',
    restore: 'Restaurar',
    delete: 'Eliminar',
    ariaLabel: 'Acciones del agente',
  },

  // ── Header del chat ────────────────────────────────────────────────
  chatHeader: {
    renameTooltipDesktop: 'Doble clic para renombrar',
    renameTooltipMobile: 'Toca para renombrar',
    moreActions: 'Más acciones',
    backAriaLabel: 'Volver a la lista de agentes',
  },

  // ── Chat — estado vacío ────────────────────────────────────────────
  chat: {
    empty: {
      title: 'Elige un agente o crea uno nuevo',
      subtitle: 'Selecciona un agente del sidebar para ver sus mensajes, enviar un seguimiento o detener la ejecución.',
      openSidebar: 'Mostrar lista de agentes',
    },
    // Sprint S20.5 PR-4 (D013-EC-70) — pill del chat-header para el
    // estado `waiting`. Espejo del badge del sidebar pero con una pista
    // más larga porque el chat header tiene más espacio horizontal y es
    // la superficie principal del agente seleccionado. Reviewer fix
    // (PR #149): el copy en castellano evita el préstamo "Idle" y usa
    // "Inactivo" para mantener `founder-first-vision.md` §5 (plain
    // Spanish — el founder lee la dashboard en es).
    idleHint: 'Inactivo · Envía un mensaje para continuar.',
  },

  // ── Composer ────────────────────────────────────────────────────────
  composer: {
    heading: 'Nuevo agente',
    openSidebar: 'Abrir sidebar',
    placeholder: {
      new: '¿Qué tiene que hacer este agente?',
      continue: 'Envía un mensaje para continuar...',
      waiting: 'Envía un mensaje · / para comandos · @ para menciones',
      running: 'Añade un seguimiento…',
      stuck: 'Envía un mensaje — el agente lo recibirá.',
      finished: 'Envía un mensaje para continuar este agente…',
      interrupted: 'Envía un mensaje para continuar este agente…',
      errorRecoverable: 'Envía un mensaje para continuar (se reintentará)…',
      errorUnrecoverable: 'Bifurca para iniciar una nueva conversación',
      idle: 'Envía un mensaje…',
      working: 'Añade un seguimiento…',
      disconnected: 'Se enviará al reconectar',
      archived: 'Este agente está archivado. Restáuralo para continuar.',
      permissionPending: 'Esperando tu decisión de permiso arriba',
      default: 'Envía un mensaje…',
      exampleHint: 'Lee el README y resúmelo en 5 puntos...  ·  escribe / para comandos, @ para menciones',
    },
    hint: {
      running: 'Esperando a que el agente termine el turno actual…',
      stuck: 'Sin actividad durante varios minutos — el proceso sigue activo pero puede estar atascado.',
      finished: 'Agente listo — envía para continuar o bifurca para ramificar.',
      interrupted: 'Agente detenido — envía para reanudar o bifurca para ramificar.',
      error: 'Agente falló — envía para reintentar, o bifurca desde el último turno funcional.',
    },
    send: 'Enviar',
    sending: 'Enviando…',
    starting: 'Iniciando…',
    stop: 'Parar',
    stopping: 'Parando…',
    stopAriaLabel: 'Parar agente',
    sendAriaLabel: 'Enviar mensaje',
    attachAriaLabel: 'Adjuntar archivo',
    promptLabel: 'Prompt',
    promptTooLong: 'Prompt demasiado largo — elimina {count} caracteres para enviar.',
    promptApproachingLimit: 'Acercándose al límite del prompt.',
    enterToSend: 'Enter para enviar · Shift + Enter para nueva línea',
    memoryWarning: 'Memoria del servidor baja — agentes nuevos pausados hasta que se libere RAM.',
    couldNotStart: 'No se pudo iniciar el agente.',
    couldNotChangeAgent: 'No se pudo cambiar el agente',
    sendFailed: 'Mensaje no enviado. Toca para reintentar.',
    attachmentLost: 'Una imagen se perdió. Vuelve a adjuntarla y envía de nuevo.',
    attachmentsLost: 'Algún adjunto se perdió. Vuelve a adjuntarlos y envía de nuevo.',
    // Sprint attachments-anthropic-parity Phase D — etiquetas accesibles
    // para la miniatura clickable + lightbox de imagen (D7, D8, D9).
    imageLightboxOpen: 'Ver imagen completa',
    imageLightboxClose: 'Cerrar imagen',
    removeAttachmentAriaLabel: 'Quitar adjunto',
    retryAttachmentAriaLabel: 'Reintentar adjunto',
    archived: {
      message: 'Este agente está archivado y ya no recibe mensajes. Si quieres seguir con él, desarchívalo desde la lista.',
    },
    unrecoverable: {
      message: 'El agente terminó con un error y no se puede reanudar. Bifúrcalo para iniciar una nueva conversación desde este punto.',
      forkCta: 'Bifurcar conversación',
    },
    confirmModeSwitch: {
      toPlan: '¿Cambiar a modo Plan?',
      toAgent: '¿Cambiar a modo Agente?',
      body: 'Una acción está esperando tu aprobación. Cambiar de modo la cancelará.',
      confirmPlan: 'Cambiar a Plan',
      confirmAgent: 'Cambiar a Agente',
    },
  },

  // ── Diálogos de confirmación ───────────────────────────────────────
  confirm: {
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    working: 'Procesando…',
    stopAgent: {
      title: '¿Parar este agente?',
      body: 'El proceso de Claude actual se terminará. El historial del chat queda en el archivo.',
      confirm: 'Parar',
    },
    forkAgent: {
      title: '¿Bifurcar este agente?',
      body: 'Un nuevo agente retoma la misma sesión de Claude. Ambos comparten historial hasta este punto y divergen después.',
      confirm: 'Bifurcar',
    },
    deleteAgent: {
      title: '¿Eliminar este agente?',
      body: 'El proceso se terminará y el agente desaparecerá de la lista activa. No se puede deshacer.',
      confirm: 'Eliminar',
    },
    deleteAgentSidebar: {
      title: '¿Eliminar agente?',
      bodyTemplate: '"{name}" se detendrá y eliminará. Tienes 5 segundos para deshacer.',
      confirm: 'Eliminar',
    },
    signOut: {
      title: '¿Cerrar sesión?',
      body: 'Necesitarás iniciar sesión de nuevo para acceder al dashboard.',
      confirm: 'Cerrar sesión',
    },
    resetSettings: {
      title: '¿Restablecer todos los ajustes?',
      body: 'Esto restaura los valores predeterminados. No se puede deshacer.',
      confirm: 'Restablecer',
    },
    changeMode: {
      title: '¿Cambiar a modo {mode}?',
      body: 'El agente se reiniciará bajo el nuevo modo. El historial del chat se conserva; el proceso de Claude que está corriendo se reinicia.',
      confirm: 'Cambiar',
    },
  },

  // ── Toasts ─────────────────────────────────────────────────────────
  toast: {
    undo: 'Deshacer',
    dismiss: 'Descartar notificación',
    agentArchived: 'Agente archivado',
    agentRestored: 'Agente restaurado',
    agentDeleted: 'Agente eliminado',
    agentStopped: 'Agente parado',
    undoFailed: 'No se pudo deshacer — el agente se eliminará en unos segundos',
    ariaLabel: 'Notificaciones',
    couldNotPin: 'No se pudo fijar el agente. Inténtalo de nuevo.',
    couldNotArchive: 'No se pudo archivar el agente. Inténtalo de nuevo.',
    couldNotRestore: 'No se pudo restaurar el agente. Inténtalo de nuevo.',
    couldNotDelete: 'No se pudo eliminar el agente. Inténtalo de nuevo.',
    couldNotRename: 'No se pudo renombrar el agente. Inténtalo de nuevo.',
    couldNotSave: 'No se pudo guardar. Inténtalo de nuevo.',
    retry: 'Reintentar',
    // Sprint 20.3 PR-10 (D013-EC-43) — etiqueta de la acción del toast
    // de spawn-error que abre el modal centralizado. El copy del toast
    // se movió a `errors.spawn.NO_RUNNER_AVAILABLE` en S20.5 PR-3
    // para que todas las ramas de spawn vivan en el mismo namespace.
    viewRunnerUsage: 'Ver uso de runners',
  },

  // ── Página de login ────────────────────────────────────────────────
  // ── Cuentas personales (docs/cuentas-personales.md) ───────────────
  cuentas: {
    titulo: 'Cuentas y sesiones',
    volver: 'Oficina',
    hasEntradoComo: 'Has entrado como {nombre}',
    salir: 'Salir',
    cargando: 'Cargando…',
    compartidaViva: 'Todavía se puede entrar con la contraseña compartida de antes. Deja de valer en cuanto Pau termine su alta.',
    quienEstaDentro: 'Quién está dentro',
    tusSesiones: 'Tus sesiones',
    estaSesion: 'esta',
    aparatoDesconocido: 'aparato desconocido',
    lineaSesion: '{navegador} · IP {ip} · desde {desde} · visto {visto}',
    cerrarEsta: 'Cerrar esta',
    echar: 'Echar',
    sinSesiones: 'No hay sesiones abiertas.',
    programas: 'Programas de la oficina',
    programasLinea: '{n} sesiones de programas automáticos del propio servidor. Solo valen desde dentro del servidor y caducan en un día.',
    cuentasTitulo: 'Cuentas',
    titular: 'Titular',
    socio: 'Socio',
    desactivada: 'desactivada',
    activa: 'activa, con Google Authenticator',
    altaPendiente: 'alta pendiente',
    enlaceNuevo: 'Enlace de alta nuevo',
    confirmarEnlaceNuevo: '¿Dar un enlace de alta nuevo a {nombre}? Su contraseña, su Authenticator y sus códigos dejan de valer y se le cierran las sesiones.',
    cerrarSusSesiones: 'Cerrar sus sesiones',
    desactivar: 'Desactivar',
    confirmarDesactivar: '¿Desactivar a {nombre}? Sale ya y no puede volver a entrar hasta que le des un enlace de alta nuevo.',
    enlaceAviso: 'Enlace de alta para {nombre} (vale una vez, hasta el {hasta}). Mándaselo por privado:',
    copiar: 'Copiar',
    usuario: 'Usuario',
    nombre: 'Nombre',
    ejemploUsuario: 'carlos',
    ejemploNombre: 'Carlos',
    darDeAlta: 'Dar de alta a un socio',
    faltanDatos: 'Pon el usuario y el nombre.',
    registroTitulo: 'Quién hizo qué',
    cuando: 'Cuándo',
    quien: 'Quién',
    que: 'Qué',
    desde: 'Desde',
    errorCarga: 'No se pudo cargar.',
    errorAccion: 'No se pudo hacer.',
    ev: {
      entrada: 'Entró',
      entradaAparatoNuevo: 'Entró desde un aparato nuevo',
      entradaCompartida: 'Entró con la contraseña compartida',
      entradaTrasAlta: 'Entró al terminar su alta',
      entradaFallida: 'Intento de entrada fallido ({detalle})',
      salida: 'Salió',
      altaCompletada: 'Terminó su alta',
      sesionCerrada: 'Cerró una sesión',
      sesionesCerradas: 'Cerró todas las sesiones de {detalle}',
      cuentaDesactivada: 'Desactivó la cuenta de {detalle}',
      recuperacionUsada: 'Usó un código de recuperación ({detalle})',
      compartidaApagada: 'Apagó la contraseña compartida',
      mensaje: 'Escribió a un agente: «{detalle}»',
      permiso: 'Respondió a un permiso',
      despliegue: 'Decidió una subida a producción',
      aprobarPlan: 'Aprobó un plan',
      cambiosPlan: 'Pidió cambios a un plan',
      pregunta: 'Respondió una pregunta de un agente',
      escribio: 'Escribió a un agente',
      contrato: 'Contrató a un agente',
      cerrarAgente: 'Pidió cerrar a un agente',
      seguir: 'Pidió que un agente siga',
      permisoHerramienta: 'Respondió a un permiso de herramienta',
      gestionCuentas: 'Gestionó cuentas',
      otra: 'Otra acción',
      noSeHizo: ' (no se hizo)',
    },
    alta: {
      tituloEnlace: 'Enlace de alta',
      enlaceIncompleto: 'Este enlace está incompleto. Pide a Pau uno nuevo.',
      errorEnlace: 'No se pudo abrir el enlace.',
      abriendo: 'Abriendo…',
      listo: 'Listo, {nombre}',
      codigosExplica: 'Estos son tus códigos de recuperación. Si pierdes el móvil, cada uno te deja entrar una vez en lugar del código de la app.',
      soloAhora: 'Solo se ven ahora: guárdalos fuera del móvil (en tu gestor de contraseñas o impresos).',
      copiar: 'Copiar',
      descargar: 'Descargar',
      guardados: 'Los he guardado en un sitio seguro',
      entrar: 'Entrar en la oficina',
      hola: 'Hola, {nombre}',
      explica: 'Vas a crear tu cuenta de la oficina. Tu usuario es «{usuario}».',
      paso1: '1. Escanea este código con Google Authenticator',
      paso1Ayuda: 'En la app: botón «+» → «Escanear un código QR».',
      qrAlt: 'Código QR para Google Authenticator',
      verClave: '¿Estás en el móvil y no puedes escanear? Escribe la clave a mano',
      ocultarClave: 'Ocultar la clave',
      paso2: '2. Elige tu contraseña',
      contrasena: 'Contraseña',
      contrasenaAyuda: 'Al menos {min} caracteres. Mejor una frase que no uses en otro sitio.',
      repite: 'Repite la contraseña',
      paso3: '3. Escribe el código que sale ahora en la app',
      codigoAria: 'Código de Google Authenticator',
      crear: 'Crear mi cuenta',
      errMin: 'La contraseña tiene que tener al menos {min} caracteres.',
      errNoCoinciden: 'Las dos contraseñas no coinciden.',
      errCodigo: 'Escribe las 6 cifras que salen ahora en la app.',
      errGenerico: 'No se pudo terminar el alta. Inténtalo otra vez.',
      fichero: 'Códigos de recuperación de la oficina ({nombre}). Cada uno vale una sola vez.',
    },
  },

  login: {
    userLabel: 'Usuario',
    userEmpty: 'Escribe tu usuario.',
    codeLabel: 'Código de Google Authenticator',
    codeHelp: 'Las 6 cifras que salen ahora en la app. Si has perdido el móvil, escribe uno de tus códigos de recuperación.',
    codeEmpty: 'Escribe el código.',
    withAccount: 'Entrar con mi cuenta',
    passwordLabel: 'Contraseña',
    passwordEmpty: 'Introduce tu contraseña.',
    signIn: 'Iniciar sesión',
    signingIn: 'Iniciando sesión…',
    couldNotSignIn: 'No se pudo iniciar sesión. Inténtalo de nuevo.',
    wrongPassword: 'Contraseña incorrecta',
    tooManyAttempts: 'Demasiados intentos — espera {seconds}s',
  },

  // ── Feed de notificaciones (Sprint notification-center) ────────────
  notifications: {
    title: 'Notificaciones',
    markAllRead: 'Marcar leídas',
    openAction: 'Ver',
  },

  // ── Página de ajustes ──────────────────────────────────────────────
  settings: {
    heading: 'Ajustes',
    backToChat: '← Volver al chat',
    display: {
      title: 'Visualización',
      description: 'Lo que ves en el chat por defecto.',
      developerMode: 'Mostrar detalles técnicos',
      developerModeDescription: 'Muestra llamadas de herramientas, diffs, comandos bash y entradas JSON en línea. Cuando está desactivado, cada llamada se resume en una línea; puedes expandir cada una con el ▽.',
    },
    notifications: {
      title: 'Notificaciones',
      description: 'Cómo te enteras de los agentes que no estás viendo.',
      tierA: 'Necesita tu acción',
      tierADescription: 'Aprobaciones y preguntas. Siempre muestra un aviso con sonido en la app; este interruptor además envía push a tus dispositivos.',
      tierB: 'Informativo',
      tierBDescription: 'Un agente termina, falla o se queda bloqueado. Aparece en la campana con un punto — sin push, sin sonido.',
      tierC: 'Problemas del sistema',
      tierCDescription: 'Algo está roto (disco, un servidor caído). Siempre te llega por Telegram y push — esto no se puede apagar.',
      sound: 'Sonido de notificación',
      soundDescription: 'Reproduce un tono corto cuando un agente necesita tu acción — incluso mientras estás viendo el dashboard.',
      pushDevice: 'Push en este dispositivo',
      pushDeviceDescription: 'Recibe notificaciones en este dispositivo aunque la app esté cerrada.',
      pushEnable: 'Activar',
      pushDisable: 'Desactivar',
      pushUnsupported: 'Este navegador no puede recibir push. En iPhone, instala primero la app: Compartir → Añadir a pantalla de inicio.',
      pushDenied: 'Las notificaciones están bloqueadas para esta app. Permítelas en los ajustes de tu dispositivo y vuelve.',
      testSend: 'Enviar notificación de prueba',
      testSending: 'Enviando…',
      testDisabledHint: 'Activa primero el push en este dispositivo y luego envía una prueba.',
      testSent: 'Enviada — mira tu dispositivo.',
      testRateLimited: 'Espera unos segundos antes de enviar otra.',
      testFailed: 'No se pudo enviar la prueba. Inténtalo de nuevo.',
      telegram: 'Enviar notificaciones a Telegram',
      telegramDescription: 'Duplica los avisos de agentes en tu grupo de Telegram. Los problemas del sistema siempre van a Telegram, independientemente de este interruptor.',
    },
    permissions: {
      title: 'Modo de permisos por defecto',
      description: 'Cómo los nuevos agentes manejan la ejecución de herramientas por defecto. Puedes cambiar por agente con el chip de modo.',
    },
    allowlist: {
      title: 'Permitidos siempre',
      description: 'Cosas que le has dicho a Claude que puede hacer sin preguntar. Olvida cualquiera y el agente preguntará la próxima vez.',
      loading: 'Cargando…',
      couldNotLoad: 'No se pudieron cargar los permisos guardados',
      empty: 'Aún no has permitido nada para siempre. Haz clic en "Permitir siempre para…" en una tarjeta de permiso para guardar una regla aquí.',
      forget: 'Olvidar',
      forgetting: 'Olvidando…',
      couldNotForget: 'No se pudo olvidar esta regla',
      showAll: 'Mostrar todos ({count})',
      toolKind: {
        Bash: 'Ejecutar comando',
        Read: 'Leer archivo',
        Edit: 'Editar archivo',
        Write: 'Escribir archivo',
        MultiEdit: 'Editar archivo',
        NotebookEdit: 'Editar notebook',
        Glob: 'Buscar archivos',
        Grep: 'Buscar en código',
        WebFetch: 'Obtener URL',
      },
    },
    language: {
      title: 'Idioma',
      description: 'Elige el idioma de la interfaz del dashboard.',
      english: 'Inglés',
      spanish: 'Español',
    },
    account: {
      title: 'Cuenta',
      description: 'Ajustes de tu cuenta.',
    },
    servers: {
      title: 'Servidores',
      description: 'Instancias VPS ejecutando agentes de la oficina.',
      addServer: 'Añadir servidor',
      name: 'Nombre',
      baseUrl: 'URL base',
      id: 'ID del servidor',
      online: 'En línea',
      offline: 'Sin conexión',
      remove: 'Eliminar',
      save: 'Guardar',
      cancel: 'Cancelar',
      empty: 'No hay servidores configurados aún. Añade un servidor para empezar a gestionar agentes en múltiples VPS.',
      namePlaceholder: 'VPS 1',
      baseUrlPlaceholder: 'https://vps1.asoasis.com',
      idPlaceholder: 'vps1',
    },
    projects: {
      title: 'Proyectos',
      description: 'Repositorios de código gestionados por agentes.',
      addProject: 'Añadir proyecto',
      name: 'Nombre',
      slug: 'Slug',
      workdir: 'Directorio de trabajo',
      defaultBranch: 'Rama por defecto',
      archive: 'Archivar',
      unarchive: 'Restaurar',
      empty: 'No hay proyectos aún. Los proyectos se crean automáticamente cuando los agentes se inician.',
      save: 'Guardar',
      cancel: 'Cancelar',
    },
    // Sprint 20.3 PR-8 — panel admin de runners.
    runners: {
      title: 'Runners',
      description: 'Servidores que ejecutan el CLI de Claude para los agentes.',
      empty: 'No hay runners registrados todavía.',
      loadError: 'No se pudieron cargar los runners.',
      refresh: 'Actualizar',
      reload: 'Recargar runners',
      colHostname: 'Hostname',
      colStatus: 'Estado',
      colAccount: 'Cuenta',
      colCap: 'Usado · 5h',
      colWeeklySonnet: 'Sonnet · esta semana',
      colWeeklyOpus: 'Opus · esta semana',
      colActive: 'Activos',
      colLastSeen: 'Visto por última vez',
      colVersion: 'Versión',
      colActions: 'Acciones',
      statusOnline: 'Online',
      statusOffline: 'Offline',
      statusDraining: 'Drenando',
      statusIncompatible: 'Versión incompatible',
      drain: 'Drenar',
      undrain: 'Reactivar',
      reEnable: 'Re-habilitar',
      confirmDrainTitle: '¿Drenar {hostname}?',
      confirmDrainBody: 'Drenar evita que se asignen nuevos agentes a {hostname} y espera a que los agentes en curso terminen antes de que el runner salga limpiamente.',
      confirmDrain: 'Drenar runner',
      unavailableReasonBlocked: 'Cuenta de Claude bloqueada. Resuélvelo con Anthropic y luego re-habilita.',
      unavailableReasonDiskFull: 'Disco lleno. Libera espacio en el runner y luego re-habilita.',
      unavailableReasonNotAuthed: 'Claude no autenticado. Ejecuta `claude login` en el runner y luego re-habilita.',
      unavailableReasonIncompatible: 'El runner está en una versión de protocolo incompatible. Despliega el binario correcto y luego re-habilita.',
      // Sprint runner-resilience-after-missing-credential §2.4.
      unavailableReasonMissingCredential: 'Una credencial de 1Password falló el healthcheck de arranque del runner. Revisa el log del runner para ver qué item falla y arréglalo en OFM. El runner se auto-recupera en ~10 min cuando se corrija.',
      // Sprint runner-op-token-single-source (defect 3) — workspace drift.
      unavailableReasonWrongWorkspace: 'El token de 1Password del runner autentica contra el workspace incorrecto. Esperado: santi-team.1password.com. El operador debe rotar /etc/asoasis/op-token vía el mecanismo OFM-resync (ver runbook).',
      drainSuccess: '{hostname} está drenando.',
      drainError: 'No se pudo drenar {hostname}: {reason}',
      undrainSuccess: '{hostname} vuelve a la rotación.',
      undrainError: 'No se pudo reactivar {hostname}: {reason}',
      clearUnavailableSuccess: '{hostname} re-habilitado.',
      clearUnavailableError: 'No se pudo re-habilitar {hostname}: {reason}',
      capUnknown: '—',
      lastSeenNever: 'nunca',
      hours: '{value}h',
      hoursOfLimit: '{value}h / {limit}h',
      activeAgents: '{count}',
    },
  },

  // ── System pills ───────────────────────────────────────────────────
  system: {
    agentStarted: 'Agente iniciado',
    newTurn: 'Nuevo turno',
    turnComplete: 'Turno completado',
    turnCompleteWithDuration: 'Turno completado · {duration}',
    agentExited: 'Agente {status}',
    switchedTo: 'Cambiado a {model} · {effort}',
    configChanged: 'Configuración cambiada',
    stopped: 'Parado',
    agentCrashed: 'Agente falló',
    reopenedConversation: 'Conversación reabierta',
    readyContinuing: 'Listo — continuando',
    // Sprint stdout-transport-replay (D013-EC-118) — copy visible y
    // clara para pérdida de transporte verificada / truncado. Sin
    // jerga: causa en claro + qué hacer a continuación.
    outputLost:
      'Parte de la respuesta del agente se perdió por un corte de red — pídele al agente que repita su última respuesta',
    outputTruncated:
      'Un mensaje del agente era demasiado grande para entregarse — pídele que lo reenvíe en partes más pequeñas',
  },

  // ── Error card ─────────────────────────────────────────────────────
  error: {
    agentFailed: 'Agente falló',
    agentFailedWithReason: 'Agente falló: {reason}',
  },

  // ── Errores de spawn (Sprint S20.5 PR-3 / D013-EC-69) ─────────────
  // Copy founder-mode para el toast cuando `POST /api/agents` (o el
  // spawn lazy en el primer mensaje) es rechazado por el backend con
  // un `code` estructurado. Cada rama dice al founder QUÉ hacer en
  // lenguaje claro; el fallback genérico preserva el texto legacy.
  errors: {
    spawn: {
      NO_RUNNER_AVAILABLE:
        'No hay servidores disponibles. Abre Ajustes \u2192 Servidores para ver el estado.',
      RUNNER_NOT_READY:
        'El servidor está iniciando. Reintenta en unos segundos.',
      CLAUDE_NOT_AUTHENTICATED:
        'Claude no está autenticado en este servidor. Pide al operador que ejecute `claude /login` en el servidor.',
      RUNNER_OFFLINE:
        'El servidor de este agente está offline. Reintenta o elige otro servidor.',
      GENERIC: 'No se pudo crear la conversación. Toca para reintentar.',
    },
  },

  // ── Banners de estado del agente ────────────────────────────────
  agentBanner: {
    stuckTitle: 'Sin actividad en {duration}',
    stuckWait: 'Esperar',
    stuckStop: 'Parar',
    errorTitle: 'Agente detenido después de {reason}',
    errorRetry: 'Reintentar',
    errorFork: 'Bifurcar desde el último turno OK',
    interruptedTitle: 'Detenido después de {duration}',
    interruptedResume: 'Reanudar',
    interruptedFork: 'Bifurcar',
  },

  // ── Banners de red / sistema ────────────────────────────────────
  networkBanner: {
    reconnecting: 'Reconectando',
    disconnected: 'Desconectado — los agentes siguen corriendo en el servidor',
    disconnectedRetry: 'Reintentar',
    memoryConstrained: 'El servidor está al límite de memoria — agentes nuevos desactivados',
    // Sprint rate-limit-banner-ssot — copy per-runner.
    // {window} = "de 5h", "semanal", "semanal de Sonnet", "semanal de Opus" o "consumo extra".
    // {time} = hora de pared (p.ej. "05:00") del reset más temprano.
    claudeMaxOneRunner: 'Has llegado al límite {window} de Claude Max en {hostname}. Se restablece a las {time}.',
    claudeMaxSomeRunners: 'Has llegado al límite {window} de Claude Max en {count} runners. El más temprano se restablece a las {time}.',
    claudeMaxAllRunners: 'Has llegado al límite {window} de Claude Max en todos los runners. El más temprano se restablece a las {time}.',
    windowFiveHour: 'de 5h',
    windowSevenDay: 'semanal',
    windowSevenDaySonnet: 'semanal de Sonnet',
    windowSevenDayOpus: 'semanal de Opus',
    windowExtraUsage: 'de consumo extra',
    viewUsage: 'Ver uso',
  },

  // Sprint 20.3 PR-5 — auto-clone surface (failure title only; the
  // body string is shipped from the backend already in the founder's
  // language preference, so we never re-translate it here).
  spawnPreparingToast: {
    failedTitle: 'No se pudo preparar el proyecto',
    retry: 'Reintentar',
    dismissAria: 'Cerrar notificación',
    dismissGlyph: '×',
  },

  // ── Banner runner no disponible ───────────────────────────────────
  // S20.3 PR-6 (variante offline) + auto-failover-on-rate-limit
  // (D013-EC-109, variante rate-limited). Mismo patrón de banner, dos
  // copys discriminadas. Founder-mode: lenguaje plano — nunca
  // "rate-limit" ni "weekly cap" en la superficie de usuario.
  runnerUnavailableBanner: {
    offline: {
      title: '{runner} está offline',
      subtitle: 'El runner de este agente no responde. Reasigna a otro runner para continuar.',
      tryAnotherRunner: 'Probar otro runner',
    },
    rateLimited: {
      title: '{runner} agotó la cuota semanal',
      subtitle:
        'El runner de este agente se quedó sin cuota semanal. Mueve esta conversación a otro runner para continuar.',
      tryAnotherRunner: 'Mover a otro runner',
    },
    busyLabel: 'Reasignando…',
    dismissAria: 'Cerrar aviso',
    dismissGlyph: '×',
    successToast: 'Reasignado a {to}.',
    errorToast: 'No se pudo reasignar: {reason}',
    genericError: 'No se pudo reasignar.',
  },

  // Sprint 20.5 PR-2 (D013-EC-68) — runner could not resume the
  // Claude child to forward a queued message. Founder voice: calm,
  // declarative, with a next action.
  sessionLostBanner: {
    title: 'Esta sesión terminó',
    subtitle: 'Pulsa retry para reanudar la conversación.',
    retry: 'Retry',
    busyLabel: 'Reanudando…',
    dismissAria: 'Cerrar aviso de sesión terminada',
    dismissGlyph: '×',
    successToast: 'Conversación reanudada.',
    errorToast: 'No se pudo reanudar: {reason}',
    genericError: 'No se pudo reanudar.',
  },

  // Sprint 20.6 PR-2 (D013-EC-72) — Anthropic descartó la sesión.
  // Reanudar es imposible por definición; la siguiente acción
  // founder es lanzar un agente nuevo (los turnos previos siguen
  // visibles, persistidos en el dashboard).
  sessionExpiredBanner: {
    title: 'Esta sesión expiró',
    subtitle: 'Anthropic descartó el contexto de la conversación. Lanza un agente nuevo para continuar.',
    newAgent: 'Lanzar agente nuevo',
    dismissAria: 'Cerrar aviso de sesión expirada',
    dismissGlyph: '×',
  },

  // Sprint 20.6 PR-2 (D013-EC-72) — toast no-bloqueante de 2 s tras
  // una respawn-on-startup exitosa. Le aclara al usuario que el
  // parpadeo fue intencional.
  respawnedToast: {
    message: 'Conversación reanudada',
  },

  // ── Live activity (Sprint 17) ──────────────────────────────────────
  liveActivity: {
    thinking: 'Pensando',
    working: 'Trabajando',
    ariaLabel: 'Actividad del agente',
  },

  // ── Todo checklist ─────────────────────────────────────────────────
  todos: {
    heading: 'Todos',
    empty: 'TodoWrite (sin elementos)',
    progress: '{completed}/{total} listos',
    cancelled: '{count} cancelados',
  },

  // ── Plan mode banner ───────────────────────────────────────────────
  planMode: {
    banner: 'Modo Plan — solo investigación, sin cambios en archivos',
  },

  // ── Subagent panel ─────────────────────────────────────────────────
  subagent: {
    label: 'Subagente',
    tools: '{count} herramienta',
    toolsPlural: '{count} herramientas',
    running: '(trabajando…)',
    error: '(falló)',
    prompt: 'Prompt',
    subagentError: 'Error del subagente',
    finalResult: 'Resultado final',
    emptyResult: '(vacío)',
    // Sprint ai-elements-rendering — árbol recursivo de subagentes.
    depthBadge: 'N{depth}',
    descendantTools: '(+{count})',
    orphanNotice: 'Salida de subagente sin su llamada padre (historial truncado)',
  },

  // ── Dual mode chip ─────────────────────────────────────────────────
  dualMode: {
    label: 'Modo desarrollador',
    disableTooltip: 'Desactivar modo desarrollador',
  },

  // ── Permisos ───────────────────────────────────────────────────────
  permissions: {
    agentWantsTo: '{agent} quiere {action}',
    allowOnce: 'Permitir una vez',
    allowAlways: 'Permitir siempre',
    deny: 'Denegar',
    deleteFolderNamed: '{agent} quiere eliminar la carpeta {name}',
    deleteFolder: '{agent} quiere eliminar una carpeta',
    deleteFileNamed: '{agent} quiere eliminar {name}',
    deleteFiles: '{agent} quiere eliminar archivos',
    pushToGithub: '{agent} quiere hacer push a GitHub',
    discardChanges: '{agent} quiere descartar cambios locales',
    changePermissions: '{agent} quiere cambiar permisos de archivo',
    changeOwnership: '{agent} quiere cambiar propietario del archivo',
    runAsAdmin: '{agent} quiere ejecutar un comando como administrador',
    downloadFromHost: '{agent} quiere descargar de {host}',
    downloadFromInternet: '{agent} quiere descargar de internet',
    installPackage: '{agent} quiere instalar {package}',
    stopProcess: '{agent} quiere detener un proceso en ejecución',
    runCommand: '{agent} quiere ejecutar un comando',
    modifyFileNamed: '{agent} quiere modificar {name}',
    modifyFile: '{agent} quiere modificar un archivo',
    fetchFromHost: '{agent} quiere obtener datos de {host}',
    fetchFromInternet: '{agent} quiere obtener datos de internet',
    searchWeb: '{agent} quiere buscar en la web',
    finishedPlanning: '{agent} terminó de planificar y quiere comenzar',
    sensitiveAction: '{agent} quiere ejecutar una acción sensible',
    insideDir: 'Dentro de {dir}.',
    pushDetails: 'Push {branch} a {remote}.',
    hardResetDetails: 'Hard-reset a {ref}. Cualquier cambio no committed se perderá.',
    forceCheckoutDetails: 'Force-checkout — cualquier cambio no committed se perderá.',
    sudoDetails: 'sudo eleva privilegios en todo el sistema.',
    usingTool: 'Usando {tool}.',
    openDevDetails: 'Abre los detalles técnicos para ver el comando exacto.',
    commandStartsWith: 'Empieza con {verb}. Abre los detalles técnicos para ver el comando exacto.',
    planToAgentBody: 'Aprobar para cambiar de modo Plan a modo Agente para el resto de este turno.',
    toolLabel: 'Herramienta: {tool}. Abre los detalles técnicos para ver la solicitud exacta.',
    showRawDetail: 'Mostrar lo que Claude va a ejecutar',
    askingPermission: 'Claude está pidiendo permiso',
    addsGlobalRule: 'Agrega una regla global: {rule}',
    couldNotSave: 'No se pudo guardar tu decisión',
    requestGone: 'Esta solicitud ya fue resuelta.',
    couldNotSaveDetail: 'No se pudo guardar tu decisión: {error}',
    allowAlwaysFor: 'Permitir siempre para',
  },

  // ── Error boundary ─────────────────────────────────────────────────
  errorBoundary: {
    title: 'Algo se rompió',
    body: 'El dashboard encontró un error inesperado y dejó de renderizar. Tus agentes en el servidor no se ven afectados — esto es solo la vista del navegador.',
    reload: 'Recargar',
    copyDetails: 'Copiar detalles',
    crashReport: 'Informe de error de la oficina de Venuz',
  },

  // ── Protected route ────────────────────────────────────────────────
  protectedRoute: {
    loading: 'Cargando dashboard…',
  },

  // ── Autocomplete dropdown ──────────────────────────────────────────
  autocomplete: {
    noResults: 'Sin resultados',
    loading: 'Buscando…',
    error: 'No se pudieron cargar sugerencias',
  },

  // ── Compartido / común ─────────────────────────────────────────────
  common: {
    cancel: 'Cancelar',
    retry: 'Reintentar',
    close: 'Cerrar',
    save: 'Guardar',
    edit: 'Editar',
    resend: 'Reenviar',
    searchNoMatch: 'No hay agentes con "{query}"',
    refresh: 'Actualizar',
  },

  // ── Tooltips ───────────────────────────────────────────────────────
  tooltip: {
    newAgent: 'Nuevo agente',
    searchAgents: 'Buscar agentes',
    archive: 'Archivar',
    restore: 'Restaurar — devolver a la lista activa',
    deletePermanently: 'Eliminar permanentemente',
    moreActions: 'Más acciones',
    stopAgent: 'Parar agente',
    notifications: 'Notificaciones',
    settings: 'Ajustes',
    account: 'Cuenta',
  },

  // ── Sticky / user message ──────────────────────────────────────────
  message: {
    edit: 'Editar',
    resend: 'Reenviar',
  },

  // ── Scroll to bottom ───────────────────────────────────────────────
  scrollToBottom: {
    label: 'Ir al final',
    newMessage: '{count} mensaje nuevo',
    newMessages: '{count} mensajes nuevos',
    ariaLabel: '{count} mensajes nuevos, ir al final',
  },

  // ── Vista de chat ─────────────────────────────────────────────────
  chatView: {
    loadingMessages: 'Cargando mensajes…',
    couldNotLoadMessages: 'No se pudieron cargar los mensajes: {error}',
    noMessagesYet: 'Aún no hay mensajes — el agente responderá pronto.',
    loadingOlder: 'Cargando historial anterior…',
  },

  // ── Message bubble ────────────────────────────────────────────────
  messageBubble: {
    empty: '(vacío)',
    toolError: 'Error de herramienta',
    toolResult: 'Resultado de herramienta',
  },

  // ── Indicador parcial ─────────────────────────────────────────────
  partialIndicator: {
    ariaLabel: 'El agente está escribiendo',
  },

  // Sprint 20.3 PR-7 (D013-EC-22) — píldora de stream truncado.
  streamTruncated: {
    label: '[truncado · recarga para refrescar]',
    ariaLabel: 'Stream en directo truncado — haz clic para recargar desde el servidor',
    icon: '⚠',
  },

  // ── Barra de búsqueda ─────────────────────────────────────────────
  searchBar: {
    placeholder: 'Buscar agentes…',
    ariaLabel: 'Buscar agentes',
    clearAriaLabel: 'Limpiar búsqueda',
  },

  // ── Spinner ───────────────────────────────────────────────────────
  spinner: {
    ariaLabel: 'Cargando',
  },

  // ── Menú contextual extra ─────────────────────────────────────────
  agentActions: {
    ariaLabel: 'Acciones del agente',
  },

  // ── Permission card extra ─────────────────────────────────────────
  permissionCard: {
    ariaLabel: 'El agente pide permiso',
    showDevDetails: 'Ver lo que el agente está por ejecutar',
  },

  // ── Tarjeta de agregación ─────────────────────────────────────────────
  aggregation: {
    running: 'ejecutando',
    errored: '{count} con error',
    completed: 'completado',
  },

  // ── Tarjeta de resumen de turno ───────────────────────────────────────
  turnSummary: {
    done: 'Listo',
    errored: 'Con errores',
    stopped: 'Parado',
    modifiedFile: '{count} archivo modificado',
    modifiedFiles: '{count} archivos modificados',
    modifiedFileIn: '{count} archivo modificado en {dir}',
    modifiedFilesIn: '{count} archivos modificados en {dir}',
    typecheck: 'Tipos',
    tests: 'Tests',
    lint: 'Lint',
    build: 'Build',
    passed: 'pasó',
    failed: 'falló',
    showCommands: 'Mostrar comandos',
    hideCommands: 'Ocultar comandos',
  },

  // ── Barra de progreso fija ────────────────────────────────────────────
  progress: {
    allDone: 'Todos los pasos listos',
    working: 'Trabajando…',
    phaseOf: 'Fase {current} de {total}',
  },

  // ── Pill de actualización de todo ─────────────────────────────────────
  todoUpdate: {
    started: 'Iniciado',
    completed: 'Completado',
    cancelled: 'Cancelado',
  },

  // ── Tarjeta de pregunta al usuario ────────────────────────────────────
  askQuestion: {
    heading: 'Claude necesita tu input',
    pickOption: 'Elige una opción abajo para continuar.',
    answerQuestions: 'Responde {count} preguntas para continuar.',
    submit: 'Enviar',
    pickAny: '(elige varias)',
    noOptions: 'No se proporcionaron opciones.',
    askingQuestion: 'Claude está haciendo una pregunta',
    couldNotSend: 'No se pudo enviar tu respuesta',
    alreadyAnswered: 'Esta pregunta ya fue respondida.',
    couldNotSendDetail: 'No se pudo enviar tu respuesta: {error}',
  },

  // ── Pill de resultado de permiso ──────────────────────────────────────
  permissionOutcome: {
    autoAllowed: 'Auto-permitido:',
    allowed: 'Permitido:',
    denied: 'Denegado:',
    decidedBy: 'Decidido por:',
    reason: 'Razón:',
    allowlistPattern: 'Patrón de lista blanca:',
    tool: 'Herramienta:',
  },

  // ── Elemento de lista de agentes ──────────────────────────────────────
  agentList: {
    working: 'Trabajando…',
    waitingForInput: 'Esperando respuesta',
    noActivity: 'Sin actividad en {min}m',
    done: 'Listo',
    diffStat: '+{added} / -{removed} · {files} archivos',
    diffStatNoRemoved: '+{added} · {files} archivos',
    unread: 'No leído',
    renameAgent: 'Renombrar agente',
    clearSearch: 'Limpiar búsqueda',
  },

  // ── Chip de modo ──────────────────────────────────────────────────────
  modeChip: {
    agent: 'Autónomo',
    plan: 'Plan',
    auto: 'Autónomo',
    agentDesc: 'Ejecuta sin pedir permiso. Las operaciones peligrosas se bloquean automáticamente.',
    planDesc: 'Solo lectura — explora y planifica sin modificar archivos',
    autoDesc: 'Ejecuta sin pedir permiso. Las operaciones peligrosas se bloquean automáticamente.',
    modeAriaLabel: 'Modo de permisos: {mode}. Haz clic para cambiar.',
  },

  // ── Chips de modelo/esfuerzo ──────────────────────────────────────────
  modelChip: {
    modelAndEffortAriaLabel: 'Modelo: {model}, esfuerzo: {effort}. Haz clic para cambiar.',
    popoverAriaLabel: 'Elige modelo y esfuerzo',
    opusOnlyTag: 'Solo Opus',
  },

  // ── Chip de rama ──────────────────────────────────────────────────────
  branch: {
    ahead: '{count} adelante',
    behind: '{count} atrás',
    aheadOfMain: '{count} adelante de main',
    behindOfMain: '{count} atrás de main',
    uncommittedChanges: 'cambios sin commit',
  },

  // ── Chip del runner (Sprint 20.3 PR-9 / D20 / UC-38) ──────────────────
  runnerChip: {
    tooltipFull: '{hostname} · {account} · {cap}',
    tooltipNoCap: '{hostname} · {account}',
    tooltipMinimal: '{runner} · uso no reportado',
    cap: 'usado {value}%',
    accountUnknown: 'cuenta no reportada',
  },

  // ── Donut de contexto ─────────────────────────────────────────────────
  contextDonut: {
    ariaLabel: 'Contexto usado {pct}%',
    defaultTooltip: 'Contexto usado: {pct}%\nDerivado en cada límite de turno a partir de los contadores de uso del CLI.',
  },

  // ── Modal de uso ─────────────────────────────────────────────────────
  // Sprint 20.3 PR-10 — repensado de agregado por cuenta OAuth a
  // agregado por runner sobre toda la topología. D17.
  runnersHealth: {
    ariaLabel: 'Estado de runners — click para ver detalles de capacidad',
    allHealthy: '{count} runners',
    allHealthyWithCap: '{count} runners · {cap}',
    singleRunner: '1 runner',
    singleRunnerWithCap: '1 runner · {cap}',
    degraded: '{online}/{total} runners',
    allOffline: 'Sin runners online',
    cap: {
      percent: '{value}% libre',
    },
    tooltip: {
      title: 'Capacidad de runners',
      capFree: '{value}% libre',
      capUnknown: 'capacidad desconocida',
      unavailable: 'no disponible',
      empty: 'Aún no hay runners registrados.',
    },
  },

  usage: {
    title: 'Uso de runners',
    description: 'Capacidad y utilización de cada runner en el despliegue.',
    loading: 'Consultando uso…',
    error: 'No se pudo obtener el uso. Inténtalo en unos minutos.',
    retry: 'Reintentar',
    empty: 'No hay runners registrados todavía.',
    colRunner: 'Runner',
    colAccount: 'Cuenta',
    colCap: 'Usado · 5h',
    colWeeklySonnet: 'Sonnet · esta semana',
    colWeeklyOpus: 'Opus · esta semana',
    colActive: 'Activos',
    colStatus: 'Estado',
    colLastSeen: 'Visto por última vez',
    statusOnline: 'Online',
    statusOffline: 'Offline',
    statusDraining: 'Drenando',
    statusIncompatible: 'Incompatible',
    footerEarliestReset: 'Próximo reset: {hostname} en {time}',
    footerNoReset: 'Ningún cap reset reportado todavía.',
    percent: '{value}%',
    resetsIn: 'Reset en {time}',
    capUnknown: '—',
    hoursOfLimit: '{value}h / {limit}h',
    // Sprint 20.8 (D013-EC-76) — etiquetas de las cinco barras canónicas
    // que devuelve Anthropic. Misma terminología que Claude Code en VS
    // Code (paridad con Cursor por cursor-ux-mirror.md).
    session5h: 'Sesión (5h)',
    weekly7d: 'Semanal (7d)',
    weeklySonnet: 'Sonnet semanal',
    weeklyOpus: 'Opus semanal',
    extraUsage: 'Uso extra',
    // Sprint usage-limits-v2 (2026-07-08) — etiquetas para el array
    // canónico `limits[]` que Anthropic ahora expone.
    limitSession: 'Sesión (5h)',
    limitWeeklyAll: 'Semanal (7d)',
    limitWeeklyScoped: 'Semanal · scoped',
    limitWeeklyScopedModel: 'Semanal · {model}',
    // Sub-etiqueta en rojo cuando el límite está activo o al 100 %.
    limitReached: 'Límite alcanzado — resetea en {time}',
    // Copy de fallo per-runner (founder-mode). Cada clave mapea a un
    // `usageErrorCode` que el bridge devuelve. Lenguaje declarativo +
    // accionable: siempre describe qué pasó y qué hacer.
    errorOauthMissing: 'No hay credenciales de Claude en {hostname}. Ejecuta `claude login` en el runner.',
    errorOauthInvalid: 'El token de Claude expiró en {hostname}. Ejecuta `claude login` en el runner.',
    errorRateLimited: 'Anthropic rate-limited el endpoint de uso para {hostname}. Reinténtalo en unos minutos.',
    errorUpstream: 'No se pudo obtener el uso de {hostname}. Reinténtalo en un momento.',
    errorTimeout: '{hostname} no respondió a tiempo. El runner probablemente está saturado — reinténtalo en breve.',
    errorRunnerOffline: 'Runner offline desde {lastSeen}. Reconéctalo para ver el uso.',
  },

  // ── Bloque de tool call ───────────────────────────────────────────────
  toolCall: {
    running: '(ejecutando…)',
    error: '(error)',
    args: 'Args',
    errorLabel: 'Error',
    result: 'Resultado',
    empty: '(vacío)',
    showLess: 'Mostrar menos',
    showMore: 'Mostrar más ({chars} caracteres)',
    inputs: 'Entradas',
    // Sprint monitor-tool-live-timer — contador en vuelo en las tool rows
    // que siguen abiertas (Monitor / cualquier tool que supere los 2 s).
    waitingSeconds: 'Esperando {s}s',
    waitingMinutes: 'Esperando {m}m {s}s',
    waitingHours: 'Esperando {h}h {m}m',
  },

  // ── Visor de documentos (D-012, sprint md-doc-viewer; D-019 descarga) ─
  doc: {
    loading: 'Cargando…',
    notFound: '{path} ya no existe en este proyecto.',
    tooLarge: 'Este documento supera los 25 MB.',
    forbidden: 'Esta ruta no está permitida.',
    sessionExpired: 'Sesión caducada. Recarga la página.',
    networkError: 'No se pudo cargar el documento. Revisa la conexión.',
    retry: 'Reintentar',
    close: 'Cerrar',
    download: 'Descargar',
    noWorkdir: 'Este agente todavía no tiene directorio de trabajo.',
    pdfIosFallback:
      'Vista previa no disponible en iOS. Toca Descargar para abrir este PDF.',
  },

  // ── Superficie de rendering AI Elements (sprint ai-elements-rendering) ─
  aiElements: {
    chainOfThought: {
      header: 'Pasos de razonamiento',
    },
    reasoning: {
      thinking: 'Pensando…',
      thoughtBriefly: 'Pensó durante unos segundos',
      thoughtForSeconds: 'Pensó durante {duration} segundos',
    },
    codeBlock: {
      copy: 'Copiar',
      copied: 'Copiado',
      copyAria: 'Copiar código al portapapeles',
      copiedAria: 'Código copiado al portapapeles',
    },
    attachments: {
      image: 'Imagen',
      file: 'Adjunto',
      remove: 'Quitar adjunto',
      uploadFiles: 'Subir archivos',
      addPhotosOrFiles: 'Añadir fotos o archivos',
      errorAccept: 'Ningún archivo coincide con los tipos permitidos.',
      errorMaxFileSize: 'Todos los archivos superan el tamaño máximo.',
      errorMaxFiles: 'Demasiados archivos. Algunos no se añadieron.',
    },
    promptInput: {
      placeholder: '¿Qué quieres saber?',
      submit: 'Enviar',
    },
  },

  // ── Oficina Virtual F1 (spec 001 T12) — pantalla /office ───────────────
  office: {
    // Oficina vieja (isometrica/Habbo) ELIMINADA del codigo por orden de Pau
    // (07-09-2026, "se tira a la basura"). Quedan solo las claves que usan
    // componentes vivos del panel: boton de la barra, acceso desde el chat
    // vacio y el resumen plegado del despertador en UserMessage.
    title: 'Oficina virtual',
    navLabel: 'Oficina virtual',
    homeAccess: '🏢 Oficina',
    wake: {
      collapsed: '⏰ Despertador · {summary}',
    },
  },
  oficinaV2: {
    versionNueva: 'Hay una versión nueva del panel con arreglos.',
    recargar: 'Recargar',
    titulo: 'Oficina',
    abriendo: 'Abriendo la oficina…',
    errorCarga: 'No se pudo cargar la oficina. Se reintenta al reconectar.',
    sinConexion: 'Sin conexión · reconectando…',
    desconocido: 'Sin nombre',
    sinTitulo: 'Encargo nuevo',
    contratar: 'Contratar agente',
    contratarCorto: 'Contratar',
    // 13-10 — galería de fotos y archivos, y buscador, de cada chat.
    galeria: {
      titulo: 'Fotos y archivos',
      verTodo: 'Ver todo ({n})',
      soloDocumentos: '{n} documentos, sin fotos',
      volver: 'Volver a la ficha',
      filtro: 'Quién lo mandó',
      todos: 'Todo',
      tuyos: 'Tuyos',
      suyos: 'Suyos',
      nada: 'Nada por aquí.',
      fotos: 'Fotos ({n})',
      documentos: 'Documentos ({n})',
      deTi: 'Tú',
      noDisponible: 'Ya no está disponible',
    },
    buscar: {
      titulo: 'Buscar en la conversación',
      abrir: 'Buscar en la conversación',
      placeholder: 'Buscar palabras en este chat…',
      cerrar: 'Cerrar la búsqueda',
      pista: 'Escribe al menos dos letras. Busca en toda la conversación, sin importar mayúsculas ni tildes.',
      buscando: 'Buscando…',
      nada: 'No hay ningún mensaje con «{q}».',
      uno: '1 mensaje',
      varios: '{n} mensajes',
      tu: 'Tú',
    },
    tiempo: { ahora: 'ahora', min: 'hace {n} min', h: 'hace {n} h', d: 'hace {n} d' },
    resumen: {
      linea: '{trabajando} trabajando',
      teNecesitaUno: '{n} te necesita',
      teNecesitanVarios: '{n} te necesitan',
      trabajando: '{n} trabajando',
      teNecesita: '{n} te necesita',
      teNecesitan: '{n} te necesitan',
    },
    equipo: {
      plegar: 'Plegar la lista',
      masOpciones: 'Más opciones',
      cuentasYAccesos: 'Cuentas y accesos',
      desplegar: 'Abrir la lista',
      contratarAlguien: 'Contratar a alguien',
      sinLeer: 'Sin leer',
      teNecesitan: 'Te necesitan',
      problemas: 'Con un fallo técnico',
      bloqueados: 'Parados por un problema',
      trabajando: 'Trabajando',
      enSuMesa: 'Parados',
      vacioTitulo: 'Tu oficina está lista',
      accion: {
        plan: 'Quiere tu visto bueno para empezar',
        pregunta: 'Tiene una pregunta para ti',
        permiso: 'Te pide permiso para algo',
        integrar: 'Ha terminado: ¿das por bueno su trabajo?',
        atasco: 'No ha podido terminar: mira qué pasa',
        bloqueo: 'Espera algo de ti para seguir',
      },
      titulo: 'Equipo',
      cuantos: '{n} en la oficina',
      vacio: 'Contrata a alguien: leerá tu encargo y te explicará cómo lo va a hacer antes de tocar nada.',
    },
    estado: {
      llegando: 'Empezando…',
      trabajando: 'Trabajando',
      esperando: 'Parado: escríbele si necesitas algo',
      cerrando: 'Terminando',
      integrando: 'Integrando su trabajo (luego sigue)',
      bloqueado: 'Parado por un problema',
      error: 'Tiene un fallo técnico',
    },
    teNecesita: {
      plan: 'Te necesita: dale el visto bueno para empezar',
      pregunta: 'Te necesita: tiene una pregunta para ti',
      permiso: 'Te necesita: te pide permiso para algo',
      integrar: 'Te necesita: ha terminado, ¿das por bueno su trabajo?',
      atasco: 'Te necesita: no ha podido terminar',
      bloqueo: 'Te necesita: espera algo de ti',
    },
    puesto: {
      porDecidir: 'Puesto por decidir',
      trabajaraComo: '{puesto}',
      investigar: 'Solo va a mirar y contarte: no cambia nada',
      cambiar: 'Va a hacer cambios',
    },
    fase: {
      orientandose: 'Leyendo tu encargo',
      esperando_contrato: 'Esperando tu visto bueno',
      trabajando: 'Trabajando',
      verificando: 'Comprobando que está bien',
      bloqueado: 'Parado por un problema',
      cerrando: 'Terminando',
      terminado: 'Terminado',
    },
    saltar: {
      atajo: 'Ctrl K',
      esc: 'Esc',
      titulo: 'Saltar a un agente',
      placeholder: 'Buscar o saltar a un agente',
      ayuda: '↑ ↓ para moverte · Intro para abrir · sin escribir nada, el primero es el que más lleva esperándote',
      contratar: 'Contratar a alguien',
      contratarPara: 'Contratar a alguien para «{texto}»',
      sala: 'Abrir la Sala de Control',
      boton: 'Buscar o saltar a un agente (Ctrl K)',
    },
    casa: {
      vista: 'Cómo ver la oficina',
      porEstado: 'Por estado',
      porSala: 'Por sala',
      ordenar: 'Ordenar',
      listo: 'Listo',
      ordenarAviso: 'Arrastra una tarjeta encima de otra para cambiarlas de sitio (con el teclado: Alt+←/→ sobre la tarjeta).',
      automatico: 'Orden automático',
      ajustes: 'Ajustes de la sala',
      contratarAqui: 'Contratar en {sala}',
      deOtro: 'De {nombre}',
      vacio: 'Nadie trabajando todavía',
      vacioTexto: 'Contrata a alguien y aparecerá aquí, en el grupo de lo que esté haciendo.',
      mover: 'Mover {nombre} {hacia}',
      antes: 'antes',
      despues: 'después',
      widgets: 'Resumen de Calypso, cerebros, cuentas y Santi',
    },
    escena: {
      acciones: 'Acciones de {nombre}',
      sala: 'Sala {sala}',
      sinSala: '{nombre} (sin sala)',
      personaje: '{nombre}, {puesto}, en {sala}. {estado}',
      mesaLibre: 'Mesa libre en {sala}: contratar aquí',
      recepcion: 'Recepción',
      recepcionContratar: 'Recepción · contratar',
      abrirChat: 'Abrir chat',
      nombreConEstado: '{nombre} · {estado}',
      corto: { te_necesita: 'te necesita', bloqueado: 'parado', error: 'fallo' },
    },
    mesas: {
      moverDeMesa: 'Mover de mesa',
      deshacer: 'Deshacer',
      movido: 'Has movido a {nombre} a la mesa {n}',
      cambiados: 'Has cambiado de sitio a {a} y {b}',
      ordenar: 'Ordenar',
      ordenarSala: 'Ordenar las mesas de {sala}',
      listo: 'Listo',
      ponerAlFinal: 'Mesa',
      ponerAlFinalLargo: 'Poner una mesa más en {sala}',
      ponerAqui: 'Poner una mesa en el sitio {n} de {sala}',
      quitar: 'Quitar la mesa {n} de {sala}',
      aviso: 'Ordenando {sala}: arrastra a un agente a otra mesa (si está ocupada, se cambian el sitio; con el teclado, las flechas) · × quita una mesa vacía · + la vuelve a poner',
      automatico: 'Volver al reparto automático',
      cambiarSitio: 'Cambiar el sitio',
      moverAqui: 'Sentar aquí',
    },
    sala: {
      titulo: 'Ajustes de la sala',
      ajustes: 'Ajustes de la sala {sala}',
      nombre: 'Nombre',
      color: 'Color',
      guardar: 'Guardar',
      quitar: 'Quitar la sala',
      quitarSi: 'Sí, quitarla',
      quitarExplica: 'La sala desaparece de la oficina; su proyecto y su trabajo no se borran. Solo se puede si no hay nadie trabajando en ella.',
    },
    chat: {
      conversacion: 'Conversación',
      conversacionCon: 'Conversación con {nombre}',
      cerrar: 'Cerrar la conversación',
      cerrarVentana: 'Cerrar',
      masAcciones: 'Más acciones',
      volver: 'Volver a la oficina',
      atras: 'Volver a la lista',
      sinAgente: 'Este agente ya no está en la oficina.',
      cargando: 'Abriendo la conversación…',
      cargandoAnteriores: 'Cargando mensajes anteriores…',
      escribiendo: 'escribiendo…',
      // 13-10 — lo que el agente escribe mientras trabaja, plegado en una línea por turno.
      pasos: {
        uno: '1 paso de trabajo',
        varios: '{n} pasos de trabajo',
        trabajando: 'Trabajando',
        abrir: 'Ver lo que fue haciendo',
        cerrar: 'Ocultar lo que fue haciendo',
      },
      traducido: 'Traducido del inglés',
      verOriginal: 'Ver el original',
      enCorto: 'En corto:',
      leerEntero: 'Leer entero',
      leerMenos: 'Ver menos',
      // 14-10 — con «en corto», el mensaje se abre DEBAJO del resumen (que no se va): no hay que releer.
      leerMensaje: 'Leer el mensaje entero',
      ocultarMensaje: 'Ocultar el mensaje',
      // 13-10 — la línea fija de «qué pasa ahora» encima del compositor.
      ahora: {
        trabajando: 'Trabajando',
        teNecesita: 'Te necesita',
        problema: 'Tiene un problema',
        parado: 'Parado',
        paradoSinMas: 'ha terminado lo que estaba haciendo y no te pide nada',
        cerrando: 'La oficina está revisando su trabajo',
        empezando: 'Empezando',
      },
      verTraduccion: 'Ver la traducción',
      escribe: 'Escribe un mensaje…',
      enviar: 'Enviar',
      adjuntar: 'Adjuntar un archivo',
      quitarAdjunto: 'Quitar {nombre}',
      subiendo: 'Subiendo…',
      alFinal: 'Ir al último mensaje',
      hoy: 'Hoy',
      ayer: 'Ayer',
      vozSinTexto: 'Nota de voz ({duracion}) · no se pudo transcribir',
      interrumpir: 'Interrumpir lo que está haciendo',
      interrumpiendo: 'Interrumpiendo…',
      cerrandoSinMensajes: 'La oficina está revisando su trabajo para integrarlo; tarda unos minutos. Mientras tanto no se le puede escribir: un cambio suyo ahora entraría sin revisar.',
      siguiente: {
        titulo: 'Siguiente:',
        mas: 'te tocan {n} más',
        masUno: 'te toca 1 más',
        ir: 'Ir',
        irA: 'Ir a {nombre}: {situacion}',
      },
      entrega: {
        pendiente: 'Se entregará en cuanto pueda',
        noEntregado: 'No entregado',
        enviado: 'Enviado',
        recibido: 'Recibido',
      },
      trabajo: {
        mirar: 'revisó cosas ({n})',
        cambiar: 'cambió cosas ({n})',
        comprobar: 'hizo comprobaciones ({n})',
        ayudantes: 'se ayudó de otros agentes ({n})',
        otros: 'otras tareas ({n})',
        fallos: 'tropiezos por el camino ({n})',
      },
      telegramEnviado: 'También te lo he mandado por Telegram.',
      telegramFallo: 'No se pudo mandar también por Telegram.',
      noSePudoUno: 'No pude abrir 1 archivo',
      noSePudieronVarios: 'No pude abrir {n} archivos',
      hito: {
        consultaGithub: 'Ha consultado cómo va algo en GitHub',
        consultaAgentes: 'Ha mirado qué otros agentes trabajan ahora',
        sinCupo: 'Se le acabó el cupo de uso en su servidor',
        trasladado: 'Lo he pasado a otro servidor con su conversación y su trabajo: sigue donde lo dejó',
        permisoAprobado: 'Le diste permiso',
        permisoHecho: 'Le diste permiso y la oficina lo ha hecho',
        permisoNoSePudo: 'Le diste permiso, pero la oficina no pudo hacerlo',
        permisoDenegado: 'Le dijiste que no',
        avisoOficina: 'La oficina le ha dado un aviso',
        contratado: 'Ha empezado: está leyendo tu encargo',
        contratoAprobado: 'Le diste el visto bueno',
        cambiosPedidos: 'Pediste cambios: {texto}',
        respondiste: 'Respondiste: {texto}',
        cierrePedido: 'Le pediste terminar',
        cierrePedidoAgente: 'Ha pedido irse él (no se lo pediste tú)',
        integrarPedido: 'Pide integrar su trabajo; después sigue trabajando',
        integrado: 'Su trabajo ya está en la versión oficial; sigue trabajando',
        integrarFallo: 'No se pudo integrar su trabajo; sigue trabajando',
        contratoPropuesto: 'Te ha explicado cómo lo va a hacer',
        pregunta: 'Te ha hecho una pregunta',
        permiso: 'Te pide un permiso',
        // WS15-D8 — al integrar una parte deja su nota de entrega y sigue; al irse, su informe de cierre.
        notaEntrega: 'Te ha dejado su nota de entrega: sigue trabajando',
        informeCierre: 'Te ha dejado su informe de cierre: se va',
        penso: 'Pensó {segundos} s',
        error: 'Ha tenido un fallo técnico. Si se repite, avísame.',
        esperaConfirmacion: 'Espera una confirmación que no puede pedirte por aquí. Avísame.',
      },
    },
    accion: {
      siHazlo: 'Sí, hazlo',
      plan: 'Antes de empezar, te explica cómo lo va a hacer',
      planPreguntasUna: 'Y te hace 1 pregunta.',
      planPreguntasVarias: 'Y te hace {n} preguntas.',
      revisarPlan: 'Leer y dar el visto bueno',
      bloquea: 'No puede seguir sin tu respuesta.',
      noBloquea: 'Sigue trabajando mientras tanto.',
      recomendada: 'Lo que recomienda',
      porQueRecomienda: 'Por qué lo recomienda:',
      tuRespuesta: 'Tu respuesta',
      responder: 'Responder',
      pidePermiso: 'Te pide permiso para:',
      verDetalles: 'Ver detalles',
      ocultarDetalles: 'Ocultar detalles',
      recomendado: 'recomendado',
      oEscribele: 'o escríbele',
      porQue: 'Por qué:',
      riesgo: 'Qué puede salir mal:',
      deshacer: 'Cómo se deshace:',
      permisoPronto: 'Aprobarlo desde aquí llegará pronto; mientras tanto no lo hará por su cuenta.',
      noLoHagas: 'No, no lo hagas',
      integrar: 'Ha terminado. ¿Das por bueno su trabajo?',
      revisorOk: 'Otro agente lo ha revisado y lo ve bien:',
      revisorReparos: 'Otro agente lo ha revisado y lo ve bien, con pegas:',
      revisorNo: 'Otro agente lo ha revisado y no lo daría por bueno:',
      siIntegrar: 'Sí, darlo por bueno',
      noIntegrar: 'No',
      atasco: 'No ha podido terminar',
      seguir: 'Seguir trabajando',
      forzar: 'Terminar sin guardar su trabajo',
      forzarConfirma: 'Se irá sin que su trabajo entre en el proyecto. Lo que haya hecho se guarda aparte y no se pierde.',
      forzarSi: 'Sí, terminar así',
      cancelar: 'Cancelar',
      bloqueado: 'Espera algo de ti para seguir',
      sinEntregar: 'Algunos mensajes no le han llegado',
      sinEntregarEncargo: 'El encargo inicial',
      sinEntregarOrden: 'Una respuesta tuya',
      reenviar: 'Reenviar',
      yaNoAplica: 'ya no aplica',
    },
    plan: {
      titulo: 'Cómo lo va a hacer',
      como: 'Cómo lo va a hacer',
      entiendo: 'Lo que ha entendido',
      preguntaUna: 'Te pregunta',
      preguntasVarias: 'Te pregunta ({n})',
      soloMira: 'Solo va a mirar y contarte: no cambia nada',
      puesto: 'Puesto que ha elegido: {puesto}',
      decideTu: 'Decide tú',
      siNoContestas: '— si no contestas: {texto}',
      veoMal: 'Lo que ve mal',
      propone: 'Propone:',
      cambialo: 'Tienes razón, cámbialo',
      dejalo: 'Déjalo así',
      veoMalAyuda: 'Si no marcas nada en un punto, no lo tocará sin preguntarte.',
      objetivos: 'Estará hecho cuando',
      objetivosInvestigar: 'Preguntas que responderá',
      pasos: 'Pasos',
      supuestos: 'Da por hecho que',
      entregables: 'Lo que te entregará',
      faltaUna: 'Falta 1 respuesta',
      faltanVarias: 'Faltan {n} respuestas',
      pedirCambios: 'Pedir cambios',
      correccion: '¿Qué quieres que cambie?',
      enviarCambios: 'Enviar',
      aprobar: 'Dar el visto bueno',
    },
    informe: {
      verDatos: 'Ver los datos',
      grafica: 'Gráfica',
      recomendada: 'Recomendada',
      imagen: 'Imagen',
      imagenNoDisponible: 'Imagen no disponible',
      recomendacion: 'Mi recomendación',
      decision: 'Lo que tienes que decidir',
      riesgo: 'Riesgo',
      bien: 'Lo que ya está',
      nota: 'Nota',
    },
    visorDocumento: {
      informe: 'Informe',
      descargar: 'Descargar',
      abriendo: 'Abriendo…',
      error: 'No se ha podido abrir. Inténtalo otra vez.',
      abrir: 'Abrir {nombre}',
      etiquetaInforme: 'Informe',
      leer: 'Leer',
    },
    visor: {
      ver: 'Ver {nombre} en grande',
      abrirAparte: 'Abrir aparte',
      cerrar: 'Cerrar',
    },
    fijar: {
      poner: 'Fijar arriba',
      quitar: 'Quitar de arriba',
      error: 'No se ha podido fijar. Inténtalo otra vez.',
    },
    info: {
      volverAlChat: 'Volver a la conversación',
      titulo: 'Información',
      renombrar: 'Cambiar el nombre',
      nombre: 'Nombre del agente',
      guardarNombre: 'Guardar',
      nombreLargo: 'Entre {min} y {max} letras',
      ver: 'Ver la información',
      ocultar: 'Ocultar la información',
      deQueVa: 'De qué va esto',
      ahora: 'Ahora mismo',
      pensando: 'Pensando:',
      siguiente: 'Siguiente:',
      actualizado: 'Actualizado a las {hora}',
      sinNoticias: 'Sin noticias desde hace rato',
      progreso: 'Progreso',
      objetivos: '{hechos} de {total} objetivos',
      sinPlan: 'Aún no te ha explicado cómo lo va a hacer.',
      criterio: {
        cumplido: 'Cumplido',
        en_curso: 'En curso',
        pendiente: 'Pendiente',
        fallando: 'Fallando',
        aparcado: 'Aparcado',
      },
      riesgos: 'Riesgos que ve',
      sinComprobar: 'Aún sin comprobar',
      informe: 'Informe de cierre',
      notaEntrega: 'Su última nota de entrega',
      integrado: 'Su trabajo quedó guardado en el proyecto con tu visto bueno',
      sinIntegrar: 'Terminado sin guardar su trabajo en el proyecto (queda aparte)',
      noHecho: 'No hecho:',
      noHechoLinea: '{que} — {por_que}',
      siContinuas: 'Si alguien continúa:',
      gasto: 'Gastó:',
      gastoLinea: '{dolares} $ — {en_que}',
      gastoSolo: '{dolares} $',
      verPropuesta: 'Ver la propuesta de cambios',
      encargo: 'Encargo',
      paraQue: 'Para qué:',
      estaraBien: 'Estará bien si:',
      tareas: 'Sus tareas',
      detalles: 'Detalles',
      sala: 'Sala',
      modelo: 'Modelo',
      desde: 'Desde',
      historial: 'Historial ({n})',
      verMas: 'Ver más',
      verMenos: 'Ver menos',
      // 13-10 — la ficha: las seis preguntas de Pau al abrir un chat.
      ficha: {
        necesita: 'Qué necesita de ti',
        porQue: 'Por qué está',
        hecho: 'Qué hemos hecho',
        ahora: 'Qué hace ahora',
        despues: 'Qué hará después',
        nadaAun: 'Todavía nada',
        noNecesita: 'Ahora no te necesita',
        cerrando: 'Nada: la oficina está revisando su trabajo',
        problema: 'Nada: tiene un problema técnico y lo arregla la oficina',
        desde: 'desde {hace}',
        actualizada: 'Actualizado {hace}',
        vieja: 'Sin actualizar desde {hace}',
        sinFicha: 'Se rellena sola al acabar su primer turno de trabajo.',
        masDetalles: 'Más detalles',
        loQuePediste: 'Lo que le pediste',
      },
    },
    cierre: {
      boton: 'Terminar este trabajo',
      titulo: '¿Damos por terminado este trabajo?',
      progreso: 'Progreso: {hechos} de {total} objetivos cumplidos.',
      paso1: 'Termina o aparca lo pendiente, comprueba que todo funciona, guarda lo hecho y deja una nota para quien siga.',
      paso2: 'Otro agente revisa su trabajo contra lo que acordasteis.',
      paso3: 'Te pide que des por bueno su trabajo, con lo que opina el revisor en pocas palabras.',
      paso3Investigar: 'Te pide que des por bueno su informe, con lo que opina el revisor en pocas palabras.',
      tarda: 'Suele tardar entre 3 y 10 minutos.',
      avisoPregunta: 'Te preguntó «{pregunta}» y no has contestado. Si cierras, decidirá él y lo dejará anotado.',
      avisoTrabajando: 'Está trabajando: el cierre empezará cuando termine lo que está haciendo.',
      cerrarBien: 'Terminar bien',
      todaviaNo: 'Todavía no',
      despedir: 'Despedir sin recoger',
      despedirConfirma: '¿Seguro? Se irá sin guardar ni revisar nada. Lo que no haya guardado se perderá.',
      despedirSi: 'Sí, despedir',
      integrando: 'Integrando su trabajo, sin irse: {paso}',
      paso: {
        esperando_informe: 'Terminando: está recogiendo y escribiendo su informe de cierre',
        comprobando: 'Terminando: la oficina está comprobando su trabajo',
        revisando: 'Terminando: otro agente está revisando su trabajo',
        esperando_permiso: 'Terminando: espera a que des por bueno su trabajo',
        publicando: 'Terminando: guardando su trabajo en el proyecto',
        en_cola: 'Terminando: en la cola para guardar su trabajo',
        archivando: 'Terminando: recogiendo su mesa',
      },
    },
    voz: {
      nota: 'Grabar una nota de voz',
      grabando: 'Grabando una nota de voz',
      escCancela: 'Esc para cancelar',
      casiMaximo: 'Queda poco (máximo 3 minutos)',
      cancelar: 'Cancelar la nota',
      enviar: 'Enviar la nota',
      lista: 'Nota de voz lista ({duracion})',
      descartar: 'Descartar la nota',
      reintentar: 'Reintentar',
      sinPermiso: 'Activa el micrófono para este sitio en la barra del navegador.',
      noSoportado: 'Este navegador no puede grabar notas de voz.',
    },
    contratarVentana: {
      adjuntar: 'Adjuntar capturas o archivos (también puedes pegarlas aquí)',
      adjuntarMas: 'Añadir más',
      quitarAdjunto: 'Quitar {nombre}',
      adjuntosMensaje: 'Te adjunto las capturas y archivos que te decía en el encargo.',
      adjuntosFallo: 'No se pudo mandar {n} de los archivos adjuntos: pásaselo en su chat.',
      titulo: 'Contratar a alguien',
      explica: 'Cuéntale lo que necesitas como se lo contarías a una persona. Leerá esto, mirará el proyecto y te explicará cómo lo va a hacer antes de tocar nada.',
      placeholder: 'Por ejemplo: «Los fans de Maddison dicen que el bot les contesta en inglés. Averigua por qué y cuántas veces pasa, sin cambiar nada.»',
      atajo: 'Ctrl + Enter para contratar',
      necesita: 'Lo que necesitará saber',
      resultado: '¿Qué quieres conseguir?',
      paraQue: '¿Para qué lo necesitas? ¿Qué lo ha provocado?',
      comoSabre: '¿Cómo sabrás que está bien hecho?',
      noLoSe: 'No lo sé: que me ayude a definirlo',
      faltaMaterial: 'Mencionas algo que no has pegado: pégalo aquí para que lo vea tal cual.',
      secreto: 'Esto parece una clave. En un chat se queda guardada para siempre: quítala antes de contratar.',
      quien: 'Quién lo hace',
      sala: 'Sala',
      modelo: 'Modelo',
      potencia: 'Potencia',
      potenciaAyuda: 'Más potencia piensa más cada paso: mejor para lo difícil, más lento para lo sencillo.',
      contexto: 'Añadir contexto',
      contextoAyuda: 'Opcional: para qué, cómo sabrás que está bien, qué no tocar… Si falta, te lo preguntará antes de empezar.',
      noTocar: '¿Qué NO debe tocar?',
      nombre: 'Nombre del agente',
      nombreAyuda: 'Si lo dejas vacío, se le pone uno.',
      tema: 'Título (opcional)',
      temaAyuda: 'Lo que verás en la oficina para reconocerlo. Si lo dejas vacío, lo escribe la recepción a partir de tu encargo.',
      contratar: 'Contratar a {nombre}',
      contratarSinNombre: 'Contratar',
      descartar: '¿Descartar lo que has escrito?',
      llegado: 'Ha llegado a {sala}: «{nombre}»',
      ver: 'Ver',
      sinSalas: 'Todavía no hay ninguna sala habilitada.',
    },
    // 01-11 — los contratos con Santi. 03-11: la ventana «Santi» nueva (docs/pelotas/ux-2026-11-03/propuesta/propuesta.md;
    // sin pelota, ni ficha, ni «Cancelar todos», ni «General»: «Chat con Santi»).
    pelotas: {
      pau: 'Pau',
      santi: 'Santi',
      tu: 'Tú',
      // 02-11 — lo que ve un socio en lugar de lo que sale hacia Santi como Pau (la caja, los botones de una tarjeta).
      loEnviaPau: 'Lo que le llega a Santi lo envía Pau.',
      contador: '{n}/{max}',
      lleva: { min: '{n} min', h: '{n} h', dia: '1 día', dias: '{n} días' },
      pantalla: {
        titulo: 'Santi',
        nadaNuevo: 'Nada nuevo',
        nuevo: 'nuevo',
        nuevos: 'nuevos',
        yMas: '{titulo} y {n} más',
        vencio: 'Venció',
        venceHoy: 'Vence hoy',
        sinConexion: 'Sin conexión',
        sinEnviar: 'Sin enviar',
        abrir: 'Abrir la ventana de Santi',
      },
      lista: {
        titulo: 'Contratos con Santi',
        pedirAlgo: 'Pedir algo a Santi',
        chat: 'Chat con Santi',
        deQuien: '{quien}: {texto}',
        teToca: 'Te toca',
        esperandoA: 'Esperando a {quien}',
        hechoSemana: 'Hecho esta semana',
        hechoPor: 'Hecho por {quien}',
        canceladoPor: 'Cancelado por {quien}',
        ti: 'ti',
        vacioTitulo: 'Nada pendiente con Santi',
        vacioTexto: 'Cuando Santi te pida algo, aparecerá aquí.',
        sinConexion: 'Sin conexión con Santi ahora mismo',
        sinConexionDesde: 'Sin conexión con Santi ahora mismo · lo que ves es de {hace}',
        nuevo: 'Nuevo',
        cargando: 'Cargando los contratos con Santi',
      },
      fecha: {
        hoy: 'hoy',
        ayer: 'ayer',
        el: 'el {fecha}',
        venceHoy: 'Vence hoy',
        venceManana: 'Vence mañana',
        venceEl: 'Vence el {dia}',
        vencioEl: 'Venció el {dia}',
        hoyCorto: 'Hoy',
        mananaCorto: 'Mañana',
        elCorto: 'El {dia}',
        vencioCorto: 'Venció',
        paraHoy: 'Para hoy',
        paraManana: 'Para mañana',
        paraEl: 'Para el {dia}',
        teLoPidio: 'Te lo pidió {quien} {cuando}',
        seLoPediste: 'Se lo pediste tú {cuando}',
      },
      ventana: {
        titulo: 'Santi',
        cerrar: 'Cerrar',
        verLista: 'Contratos con Santi',
        teToca: 'Te toca:',
        esperandoA: 'Esperando a {quien}:',
        chatSub: 'Lo del día a día. Lo que haya que seguir, apúntalo como contrato.',
        hecho: 'Hecho',
        cancelar: 'Cancelar',
        cancelado: 'Cancelado',
        masAcciones: 'Más acciones',
        contratarAgente: 'Contratar un agente para esto',
        verAgente: 'Ver el agente que lo lleva',
        cargando: 'Abriendo la conversación…',
        sinMensajes: 'Todavía no hay nada escrito aquí.',
        nuevo: 'Nuevo',
        hechoEl: 'Hecho el {fecha}.',
        canceladoEl: 'Cancelado el {fecha}.',
      },
      chat: {
        escribe: 'Escribe a Santi…',
        enviar: 'Enviar',
        pistaLeToca: 'Al enviar, le toca a Santi',
        pistaSigues: 'Al enviar, te sigue tocando a ti',
        pistaLeTocaCorta: 'Le toca a Santi',
        pistaSiguesCorta: 'Te sigue tocando',
        noSigoYo: 'No, sigo yo',
        queLeToque: 'Que le toque a Santi',
        noSePudo: 'No se ha podido enviar. Vuelve a probar.',
        textoLargo: 'El mensaje es demasiado largo: como mucho {max} letras.',
      },
      pedir: {
        titulo: 'Pedir algo a Santi',
        sub: 'Le llega de tu parte. Lo verás en «Esperando a Santi».',
        vacioTitulo: 'Una cosa nueva con Santi',
        vacioTexto: 'Escríbelo como se lo dirías a él. Al enviarlo, aquí mismo seguiréis hablando de ello.',
        campoTitulo: 'Título (si lo dejas vacío, lo pone tu agente)',
        campoTexto: '¿Qué necesitas de Santi?',
        paraCuando: 'Para cuándo',
        sinFecha: 'Sin fecha',
        manana: 'Mañana',
        elViernes: 'El viernes',
        otroDia: 'Otro día…',
        descartar: 'Descartar',
        tituloLargo: 'El título es demasiado largo: como mucho {max} letras.',
        textoLargo: 'Lo que necesitas de Santi es demasiado largo: como mucho {max} letras.',
        datosInvalidos: 'Santi no puede recibirlo así: el título, como mucho {titulo} letras, y lo que necesitas, como mucho {texto}.',
      },
      // 03-11 — «Hecho» y «Cancelar»: la línea de 8 s con «Deshacer» (propuesta §5.6).
      acto: {
        hecho: '«{titulo}» hecho. Se lo decimos a Santi.',
        cancelado: '«{titulo}» cancelado. Se lo decimos a Santi.',
        deshacer: 'Deshacer',
        decirPorque: 'Decirle por qué',
        porque: '¿Por qué? Lo verá Santi',
        enviar: 'Enviar',
        falloHecho: 'No se ha podido marcar como hecho «{titulo}». Vuelve a probar.',
        falloCancelar: 'No se ha podido cancelar «{titulo}». Vuelve a probar.',
        volverAProbar: 'Volver a probar',
        quitarAviso: 'Quitar el aviso',
        entreComillas: '«{titulo}»',
      },
      agente: {
        titulo: 'Tu agente',
        dePau: 'El agente de Pau',
        sub: 'Para todo lo de Santi. Santi no ve esto.',
        cerrar: 'Cerrar tu agente',
        escribe: 'Dile algo a tu agente…',
        sinAgente: 'Un agente se pone con esto',
        sinAgenteGeneral: 'Para echarte una mano mientras hablas con Santi.',
        poner: 'Ponerle un agente',
        poniendo: 'Llegando…',
        cargando: 'Abriendo el chat…',
      },
      // La tarjeta «Para Santi» (hasta la entrega 2: entonces el agente deja borradores en la caja).
      tarjeta: {
        paraSanti: 'Para Santi',
        contratoCon: 'Contrato con Santi',
        lePasas: 'Al enviarlo, le toca a Santi.',
        usar: 'Usar en el chat',
        enviar: 'Enviar a Santi',
        cerrar: 'Cerrar contrato',
        cambiar: 'Cambiar algo',
        seguirHablando: 'Seguir hablando',
        enviado: 'Enviado a Santi · {hora}',
        teToca: 'Te toca',
        esperandoA: 'Esperando a {quien}',
        desdeHace: 'desde hace {lleva}',
      },
    },
    despliegues: {
      filaContrasena: 'Fija tu contraseña del Allow',
      filaContrasenaExplica: 'Sin ella no puedes aprobar ni devolver despliegues desde aquí. Es un momento.',
      pedidoDesplegar: 'Se pidió desplegar',
      creoQueLoPidio: 'Creo que lo pidió:',
      vuelveTrasNota: 'Vuelve tras tu nota: «{nota}»',
      masInfoNoDisponible: 'No hay explicación larga para este trabajo. Pregúntale a quien lo pidió o pide una segunda opinión.',
      rechazarSinMas: 'Rechazar sin más',
      allowIgualmente: 'Allow igualmente',
      sinContestar: 'Aún no te ha contestado a «{pregunta}». Si le das al Allow ahora, lo haces sin su respuesta.',
      esperaDesde: 'Espera tu Allow desde {hace}',
      loPidio: 'Lo ha pedido:',
      noSeQuien: 'no sé qué agente lo pidió',
      yaNoEsta: 'ya no está en el panel',
      hablarCon: 'Hablar con él',
      vistaResumen: 'Resumen',
      vistaInfo: 'Más información',
      vistaHablar: 'Hablar',
      vistas: 'Qué quieres ver',
      puntoPorPunto: 'Qué cambia, punto por punto',
      porQue: 'Por qué se hace',
      comoSeComprobo: 'Cómo se ha comprobado',
      quePodriaSalirMal: 'Qué podría salir mal',
      queToca: 'Partes de Calypso que toca',
      masInfoPreparando: 'Preparando la explicación completa…',
      masInfoFuente: 'Explicación escrita por IA a partir de la propuesta del agente y de los cambios reales. Si algo no cuadra, pregúntale.',
      segundaOpinion: 'Pedir una segunda opinión',
      segundaOpinionExplica: '¿Quieres que otro agente lo revise a fondo y lo discuta contigo? Aparecerá en la oficina y hablarás con él por su chat.',
      segundaOpinionEncargo: 'Hay un despliegue esperando mi Allow: «{que}». {cambia} Revísalo a fondo antes de que lo apruebe: qué cambia exactamente, si tiene sentido, qué riesgos tiene y si se ha comprobado bien. Explícamelo sin tecnicismos y responde mis dudas. No cambies nada ni lo apruebes: solo revisa y opina. (Para ti: el despliegue es {enlace}.)',
      hablarExplica: 'Lo que escribas le llega a quien lo pidió, con el contexto de este despliegue. Te contesta aquí.',
      hablarVacio: 'Pregúntale lo que quieras sobre este cambio.',
      hablarEscribe: 'Escribe tu pregunta…',
      hablarNoEsta: 'Quien lo pidió ya no está en el panel, así que no puede contestarte. Puedes pedir a otro agente que lo revise contigo.',
      hablarNoSe: 'No sé qué agente lo pidió, así que no puedo pasarle tu pregunta. Puedes pedir a otro agente que lo revise contigo.',
      devolver: 'Algo no me convence',
      devolverBoton: 'Devolver para ajustar',
      devolverTitulo: '¿Qué no te convence?',
      devolverExplica: 'Escribe qué quieres que cambie. Se lo digo a «{quien}» con tu nota, lo que toca a los fans sale de la versión oficial y te lo volverá a pedir ajustado. Si no quieres que se haga, «Rechazar sin más».',
      devolverExplicaSinQuien: 'Quien lo pidió no está disponible, así que tu nota queda apuntada en el registro. Si no quieres que se haga, «Rechazar sin más».',
      devolverNota: 'Por ejemplo: «No quiero que afecte a todas las cuentas a la vez; empieza por una.»',
      devuelto: 'Devuelto a «{quien}»: te lo volverá a pedir con tu nota',
      loLanzo: 'Lo ha pedido un agente {hace}',
      queCambia: 'Qué cambia',
      queApruebas: 'Qué apruebas',
      siSaleMal: 'Si sale mal',
      siRechazas: 'Si lo rechazas',
      queSubeUno: 'Qué sube: 1 cambio',
      queSubeVarios: 'Qué sube: {n} cambios, de todos los agentes',
      ningunoFans: 'Ninguno toca lo que contesta a los fans.',
      restoUno: '1 cambio más que no toca a los fans',
      restoVarios: '{n} cambios más que no tocan a los fans',
      yMas: 'Y {n} más que no caben aquí: pregunta al agente antes de darle al Allow.',
      sinResumenCambio: 'Sin resumen del agente: es lo que dice el título.',
      verCambio: 'Ver el cambio en GitHub',
      vuelveRechazado: 'Vuelve algo que rechazaste antes',
      esRetirada: 'Saca algo que rechazaste (si rechazas la subida, esto no se deshace)',
      filaNingunoFans: 'Nada de lo que sube toca a los fans',
      filaFansUno: '1 cambio toca a los fans: {quienes}',
      filaFansVarios: '{n} cambios tocan a los fans: {quienes}',
      preparando: 'Preparando la explicación…',
      sinResumen: 'No he podido resumirlo. Si no sabes qué es, pregunta al agente antes de darle al Allow.',
      detallesTecnicos: 'Detalles técnicos (para los agentes)',
      tituloUno: '1 subida espera tu Allow',
      tituloVarios: '{n} subidas esperan tu Allow',
      conAviso: '{n} con aviso',
      soloLectura: 'Diagnóstico: solo lee, no cambia nada',
      soloLecturaExplica: 'Es un diagnóstico de solo lectura: no cambia nada en producción. Pide tu Allow porque todavía comparte el entorno de producción con los despliegues.',
      muchosDiagnosticos: 'Son diagnósticos de solo lectura: cuando se muevan a su propio entorno dejarán de pedirte el Allow.',
      version: 'Versión',
      propuesta: 'propuesta #{n}',
      lanzadoPor: 'Lanzado por',
      cuando: 'Cuándo',
      cambiosEnv: 'Cambios de ajustes (.env) que lleva',
      sinCambiosEnv: 'Ninguno: los ajustes de producción se quedan como están.',
      comoSeDeshace: 'Cómo se deshace',
      verEnGithub: 'Ver en GitHub',
      tuContrasena: 'Tu contraseña del Allow',
      tuCodigo: 'Código de tu Google Authenticator',
      allow: 'Allow',
      rechazar: 'Rechazar',
      aprobado: 'Allow dado: «{workflow}» sigue adelante',
      rechazado: 'Rechazado: «{workflow}» no se hará',
      bloqueado: 'Demasiadas contraseñas fallidas: bloqueado hasta las {hora}. Te he avisado por Telegram.',
      sinContrasena: 'Todavía no tienes contraseña del Allow. Fíjala una vez y ya podrás aprobar desde aquí.',
      fijarBoton: 'Fijar mi contraseña del Allow',
      fijarTitulo: 'Tu contraseña del Allow',
      fijarExplica: 'Solo se fija una vez, con la contraseña del panel. Se guarda cifrada, te la pediré en cada Allow y te avisaré por Telegram cada vez que se fije o se use. Ojo: mientras los agentes compartan tu cuenta de GitHub, esta contraseña es tu puerta y tu alarma, no un cerrojo contra ellos.',
      contrasenaPanel: 'Contraseña del panel',
      nueva: 'Nueva contraseña del Allow',
      nuevaAyuda: 'Al menos 10 caracteres y distinta de la del panel.',
      repite: 'Repítela',
      noCoinciden: 'No coinciden.',
      fijarGuardar: 'Guardar',
      fijada: 'Contraseña del Allow guardada',
      rama: 'Rama',
      vueltaAtras: 'Vuelta atrás a la versión {sha}',
      versionDesconocida: 'desconocida',
      cambiosEnvDesconocido: 'No he podido leerlos. No le des al Allow sin mirarlo en GitHub.',
      copiasNginx: 'Copias y nginx',
      copiasNoSeVen: 'No se ven antes del Allow (por defecto: las tres copias, sin tocar nginx).',
      copiasSi: '{copias} · {nginx}',
      nginxSi: 'también cambia nginx',
      nginxNo: 'sin tocar nginx',
      incompleta: 'No he podido leer todo lo que hay en GitHub: puede haber más esperando.',
      sinTitulo: 'sin título',
      lineas: {
        aria: 'Lo que cambia esta subida',
        cambiaUna: 'Cambia 1 cosa en producción',
        cambiaVarias: 'Cambian {n} cosas en producción',
        ninguna: 'Nada de lo que sube cambia lo que viven los fans.',
        saleCon: 'No sube: va en el mismo trabajo que «{titulo}»',
        noSube: 'No sube con este Allow',
        deshacer: 'Deshacer',
        antes: 'Antes',
        ahora: 'Ahora',
        nuevo: 'Nuevo',
        calypsoLeera: 'Calypso leerá',
        pocoSegura: 'Esta lectura no es del todo segura: si te importa, pregunta.',
        preguntarA: 'Preguntar a {quien}',
        preguntar: 'Preguntar',
        quitar: 'Quitar',
        aQuien: 'A quién toca:',
        noHablado: 'Esto no lo hablaste con {quien}',
        noHabladoSinQuien: 'Esto no lo hablaste con el agente',
        rechazado: 'Le dijiste que no:',
        lePediste: 'Lo pediste tú:',
        quitarTitulo: 'Si lo quitas, sale de esta subida:',
        trabajoDe: '«{titulo}», de {agente}',
        unAjuste: 'El ajuste de esta línea (el código no cambia).',
        variosAjustes: 'Los {n} ajustes de esta línea (el código no cambia).',
        arrastraUna: 'Va en el mismo trabajo, así que también sale:',
        arrastraVarias: 'Van en el mismo trabajo, así que también salen:',
        quitarExplica: 'Lo demás sube con este mismo Allow: el agente la vuelve a lanzar sin esto y, si no se cuela nada nuevo, sube sin volver a pedírtelo. Te aviso por Telegram.',
        quitarConfirmar: 'Quitar esta línea',
        quitarConArrastre: 'Quitar estas {n} líneas',
        hablasCon: 'Hablas con {quien} sobre esta línea. La subida espera mientras tanto.',
        hablasConQuienPidio: 'Hablas con quien pidió la subida, sobre esta línea. La subida espera mientras tanto.',
        preguntaPlaceholder: 'Pregunta o pide un cambio…',
        preguntaAria: 'Pregunta sobre «{titulo}»',
        sinEfectoUno: 'Y 1 trabajo que no cambia nada a los fans',
        sinEfectoVarios: 'Y {n} trabajos que no cambian nada a los fans',
        aprobadoQuitando: 'Allow dado quitando {n}: lo demás sube en cuanto vuelva sin eso. Te aviso por Telegram.',
        allowParcial: 'Allow a {n} · quitar {m}',
        loPide: 'Lo pide {quien}',
        creoQueLoPide: 'Creo que lo pide {quien}',
        volver: 'Volver a la subida',
        trasQuitar: 'Lo quitado sale de la versión oficial. Lo demás sube con este Allow en cuanto vuelva sin ello; te aviso por Telegram.',
        preparando: 'Leyendo qué cambia exactamente, línea por línea. Tarda 1-3 minutos; mientras, tienes el resumen de siempre.',
        noDisponible: 'No he podido preparar el detalle línea por línea de esta subida. Abajo tienes el resumen de siempre; si dudas, pregunta antes del Allow.',
        masDetalles: 'Si lo rechazas, si sale mal y detalles técnicos',
        verExplicacion: 'Explicación larga',
        hablarSubida: 'Hablar con {quien} de toda la subida',
        cita: '«{texto}»',
        filaSinHablarUna: '1 no la hablaste',
        filaSinHablarVarias: '{n} no las hablaste',
      },
    },
    s12: {
      situacion: {
        llegando: 'Empezando',
        autorizacion: 'Pide tu permiso',
        bloqueado: 'No puede seguir sin ti',
        te_escribio: 'Espera tu respuesta',
        problema: 'Tiene un problema técnico',
        trabajando: 'Trabajando',
        cerrando: 'Cerrando',
        sin_pendiente: 'Sin nada pendiente',
        saliendo: 'Se está yendo',
      },
      conSanti: { resumen: 'Lo tienen ellos · el más viejo, {cuando}', marca: 'Tiene un contrato con Santi' },
      grupo: {
        fijados: 'Fijados',
        problema_pau: 'Problemas que necesitan tu ayuda',
        autorizacion: 'Piden tu permiso',
        bloqueado: 'No pueden seguir sin ti',
        te_escribio: 'Esperan tu respuesta',
        problema: 'Con un problema técnico',
        en_marcha: 'Trabajando o esperando un proceso',
        sin_pendiente: 'Sin nada pendiente',
        se_van: 'Se están yendo',
        con_santi: 'Con Santi',
      },
      fila: {
        permisoSinTexto: 'Pide permiso para seguir',
        bloqueadoSinTexto: 'Necesita algo de ti para seguir',
        teEscribioSinTexto: 'Te ha dejado un mensaje',
        terminado: 'Terminado: {que}',
        terminadoSinTexto: 'te entrega su trabajo',
        problemaSinTexto: 'Algo técnico le impide trabajar',
        problemaOficina: '{que}. La oficina lo está arreglando.',
        trabajandoSinTexto: 'Trabajando en tu encargo',
        cerrandoSinTexto: 'La oficina está revisando su trabajo',
        integrando: 'Integrando su trabajo, sin irse: {paso}',
        llegando: 'Leyendo tu encargo',
        saliendo: 'Recogiendo su mesa',
        sinPendienteSinTexto: 'Nada pendiente',
      },
      cierre: {
        esperando_informe: 'Escribiendo su informe de cierre',
        // WS15-D8 — integrando (sigue después): lo que escribe es su nota de entrega, no un informe de cierre.
        esperando_nota: 'Escribiendo su nota de entrega',
        comprobando: 'Comprobando que todo funciona',
        revisando: 'Otro agente revisa su trabajo',
        esperando_permiso: 'Esperando a que des por bueno su trabajo',
        publicando: 'Integrando su trabajo',
        en_cola: 'En cola para integrar su trabajo',
        archivando: 'Recogiendo su mesa',
      },
      resumen: {
        permisoUno: '1 permiso',
        permisoVarios: '{n} permisos',
        bloqueadoUno: '1 no puede seguir',
        bloqueadoVarios: '{n} no pueden seguir',
        problemaPauUno: '1 problema para ti',
        problemaPauVarios: '{n} problemas para ti',
        teEscribioUno: '1 espera tu respuesta',
        teEscribioVarios: '{n} esperan tu respuesta',
        trabajando: '{n} trabajando',
        cerrando: '{n} cerrando',
        problemaUno: '1 con un problema',
        problemaVarios: '{n} con un problema',
        nadaTeToca: 'Nada te toca ahora',
      },
      sinLeer: 'Sin leer',
      fijado: 'Fijado',
      rapido: {
        si: 'Sí',
        aria: 'Sí, dar permiso a {nombre}: {que}',
        pista: 'Dar permiso sin abrir el chat',
        error: 'No se pudo dar permiso a {nombre}: {motivo}',
      },
      teEscribioTerminados: { uno: 'Esperan tu respuesta (1 ha terminado)', varios: 'Esperan tu respuesta ({n} han terminado)' },
      sinNoticias: 'sin noticias · {cuanto}',
      duracion: { min: '{n} min', h: '{n} h', d: '{n} d' },
      esperaDesde: 'Esperando desde {hace}',
      bandejas: {
        esperan: '{n} esperan a {nombre}',
        selector: 'De quién es la bandeja',
        mia: 'Mía',
        mirando: 'Estás mirando la bandeja de {nombre}',
        volver: 'Volver a la mía',
        de: 'de {nombre}',
      },
      franja: {
        permiso: 'Pide permiso: {que}',
        permisoCargando: 'Cargando lo que pide…',
        deshacer: 'Cómo se deshace: {que}',
        detalles: 'Detalles',
        ocultarDetalles: 'Ocultar detalles',
        bloqueado: 'No puede seguir: {que}',
        bloqueadoAyuda: 'Contéstale aquí abajo: en cuanto le escribas, sigue.',
        responderRapido: 'Respuestas rápidas',
      },
      maqueta: {
        boton: 'Maqueta S12: {escenario} ▾',
        vacia: 'Vacía',
        'uno-de-cada': '1 de cada estado',
        llena: 'Oficina llena',
        'nada-te-toca': 'Nada te toca',
        'chat-autorizacion': 'Chat: pide permiso',
        'chat-bloqueado': 'Chat: no puede seguir',
        'chat-te-escribio': 'Chat: te ha escrito',
      },
    },
    maqueta: {
      escenarios: 'Escenarios de la maqueta',
      boton: 'Maqueta: {escenario} ▾',
      tres: '3 agentes',
      uno: '1 agente',
      hover: 'Al pasar el ratón',
      despliegue: 'Despliegue pendiente',
      vacia: 'Vacía',
      llena: '12 agentes',
      chat: 'Chat',
      plan: 'Plan',
      contratar: 'Contratar',
    },
    errores: {
      SALA_PRINCIPAL: 'La sala principal no se puede quitar.',
      SALA_OCUPADA: 'Esa sala tiene agentes trabajando: cierra sus trabajos antes.',
      SALA_DESCONOCIDA: 'Esa sala ya no existe.',
      AUN_SE_REINTENTA: 'Ese mensaje todavía se está intentando entregar solo. Espera un poco.',
      AGENTE_CERRANDO: 'La oficina está revisando o integrando su trabajo: ahora no se le puede escribir (en cuanto acabe, sí).',
      PERMISO_NO_VALIDO: 'Ese permiso ya no sirve para integrar.',
      NO_SE_PUEDE_CERRAR: 'Ese agente no está trabajando ahora.',
      NO_ESTA_ATASCADO: 'Ese cierre ya no está atascado.',
      NO_SE_PUEDE: 'Ahora no se puede hacer eso con este agente.',
      PERMISO_YA_RESPONDIDO: 'Ese permiso ya tiene respuesta.',
      PERMISO_AUN_NO: 'Todavía no se puede aprobar esto desde la oficina.',
      RUNNER_OFFLINE: 'El servidor donde trabaja no responde ahora mismo. Vuelve a intentarlo en un momento.',
      CONTRATADO_CERRADO: 'Este agente ya terminó su trabajo.',
      FORMULARIO_INVALIDO: 'Falta algo o sobra texto en el encargo. Revisa los campos.',
      DATOS_INVALIDOS: 'Algo de lo que has enviado no encaja. Vuelve a intentarlo.',
      PROYECTO_NO_HABILITADO: 'Esa sala ya no está disponible.',
      MESA_YA_PUESTA: 'Ahí ya hay una mesa.',
      TOPE_MESAS: 'Esa sala ya tiene todas las mesas que caben.',
      MESA_DESCONOCIDA: 'Ahí ya no hay mesa. Mira la oficina otra vez.',
      MESA_OCUPADA: 'En esa mesa hay alguien: muévelo antes de quitarla.',
      ULTIMA_MESA: 'La sala se queda al menos con una mesa.',
      AGENTE_DESCONOCIDO: 'Ese agente ya no está en esa sala.',
      SALA_LLENA: 'Esa sala no tiene mesas libres. Cierra algún trabajo o elige otra sala.',
      SIN_RUNNER_CAPAZ: 'Los servidores se están actualizando. Prueba en unos minutos.',
      CAPACITY_UNKNOWN: 'Los servidores están arrancando. Prueba otra vez en un minuto.',
      NOMBRE_OCUPADO: 'Ya hay (o hubo hace poco) un agente con ese nombre. Elige otro.',
      ZONA_OCUPADA: 'Eso ya lo está haciendo otro agente: escríbele en su chat para ampliar su encargo.',
      PARECE_UNA_CLAVE: 'El encargo lleva algo con forma de clave. Quítalo antes de contratar.',
      CLAVE_USADA: 'Ese encargo ya se envió. Vuelve a pulsar para contratar de nuevo.',
      SIN_CONTRATO: 'Todavía no te ha explicado cómo lo va a hacer.',
      VERSION_DESFASADA: 'Ha cambiado cómo lo va a hacer mientras lo mirabas. Míralo otra vez.',
      CONTRATO_YA_RESPONDIDO: 'Eso ya tiene respuesta.',
      RESPUESTAS_INCOMPLETAS: 'Contesta todas sus preguntas o marca «Decide tú».',
      PREGUNTA_YA_RESPONDIDA: 'Esa pregunta ya está contestada.',
      NO_ES_DE_LA_OFICINA: 'Ese agente no es de la oficina.',
      // 01-11 — los contratos con Santi (pelotas).
      SOLO_PAU: 'Escribirle a Santi es cosa de Pau, desde su sesión personal: entra con tu usuario y el código del Authenticator.',
      SIN_LLAVE: 'La oficina todavía no tiene la llave del tablero de contratos con Santi: un agente tiene que instalarla.',
      LLAVE_CORTADA: 'La llave de la oficina para el tablero de contratos se cortó por seguridad: un agente tiene que poner una nueva.',
      PARECE_UN_SECRETO: 'El texto lleva algo con forma de clave o contraseña. Quítalo: lo que se envía a Santi no se puede borrar.',
      NO_TIENES_LA_PELOTA: 'Ahora le toca a Santi: mira la conversación y, si sigue haciendo falta, vuelve a enviarlo.',
      TEMA_CERRADO: 'Ese contrato ya está hecho.',
      IDEMPOTENCIA_REUTILIZADA: 'Puede que el anterior ya haya llegado: míralo en la conversación antes de enviarlo otra vez.',
      NO_DISPONIBLE: 'El tablero de contratos con Santi no responde ahora mismo. Vuelve a intentarlo en un momento.',
      TIEMPO: 'El tablero de contratos tardó demasiado en contestar. Vuelve a intentarlo.',
      RED: 'No se pudo llegar al tablero de contratos. Vuelve a intentarlo.',
      YA_RESUELTA: 'Esa tarjeta ya tiene respuesta.',
      NO_ENCONTRADO: 'Ese contrato o esa tarjeta ya no existe.',
      FORBIDDEN: 'Esto solo puede hacerlo Pau.',
      PROPUESTA_NO_PENDIENTE: 'Esa propuesta ya no está pendiente: el agente la volverá a proponer.',
      // 02-11 — en la ventana «Santi» estos códigos del centro significan otra cosa (o no tenían texto).
      pelotas: {
        VERSION_DESFASADA: 'El contrato cambió mientras lo mirabas (Santi movió algo). Mira la conversación y vuelve a enviarlo.',
        LIMITE: 'Demasiados envíos seguidos al tablero. Espera un momento y vuelve a intentarlo.',
        NO_ES_TUYO: 'Este lo pidió Santi: hasta que el tablero tenga la regla nueva, solo lo puede cerrar él. Díselo en la conversación.',
        TABLERO_SIN_CANCELAR: 'El tablero de contratos con Santi aún no sabe cancelar (se está actualizando). Prueba dentro de un rato o márcalo como «Hecho».',
        DEMASIADO_GRANDE: 'El mensaje es demasiado largo para el tablero. Acórtalo.',
        EPOCA_DISTINTA: 'El tablero de contratos se restauró de una copia hace un momento y la oficina se está poniendo al día. Mira la conversación y, si sigue haciendo falta, vuelve a enviarlo.',
      },
      generico: 'Algo ha fallado en el servidor. Vuelve a intentarlo en un momento.',
      red: 'No hay conexión con el servidor. Vuelve a intentarlo.',
    },
  },
  // ── Sala de Control de Calypso (Pau 30-09-2026) ───────────────────
  // 22-10-2026 — panel de cuentas de Claude (features/oficina/cuentas, docs/cuentas-claude/catalogo-situaciones.md).
  cuentasClaude: {
    titulo: 'Cuentas de Claude',
    placa: 'Claude',
    deCuantas: '{libres} de {leidas}',
    libres: 'libres',
    sinLectura: '{n} sin lectura',
    otraEn: 'otra {cuando}',
    unaEn: 'una {cuando}',
    sinLecturaHace: 'sin lectura {hace}',
    abrir: 'Cuentas de Claude: {libres} de {leidas} libres',
    cinco: '5 horas',
    semana: 'Semana',
    agotada: 'Agotada',
    fable: 'Fable {pct} %',
    pagoPorUso: 'Pagando por uso: {gastado} de {limite}',
    planMenor: 'Plan {plan}: menos capacidad',
    leidoHace: 'leído {hace}',
    leyendo: 'Leyendo…',
    sinLecturaDesde: 'Sin lectura desde hace {min} min',
    motivo: {
      espera: 'Anthropic pide esperar. La cuenta trabaja normal.',
      maquina: 'Su máquina no contesta. Sus agentes ya trabajan en otras.',
      lectura: 'No se puede leer su consumo ahora.',
    },
    caducada: 'Sesión caducada',
    caducadaNota: 'Sus agentes ya trabajan en otras cuentas.',
    impagada: 'Pago pendiente',
    impagadaNota: 'Anthropic no ha podido cobrarla. Sigue trabajando, pero la darán de baja: entra en claude.ai con {nombre} → Ajustes → Facturación y actualiza la tarjeta.',
    arreglar: 'Arréglalo',
    encargo:
      'La cuenta de Claude «{nombre}» (runner {runner}) lleva {min} min mal en el panel de cuentas: {problema}. Averigua la causa y arréglala; si necesita algo de Pau, dímelo claro.',
    cerrar: 'Cerrar',
    volver: 'Oficina',
    reconexion: {
      boton: 'Volver a conectar',
      titulo: 'Volver a conectar · {nombre}',
      paso1: 'Entra con el correo de {nombre}',
      paso2: 'Pulsa «Autorizar»',
      paso3: 'La oficina lo comprueba y se cierra sola',
      explica: 'Esta ventana es un navegador que funciona en la máquina de esta cuenta, con su misma conexión. No necesitas VPN, ni Firefox, ni Cursor.',
      noEnTuOrdenador: 'Si Claude te manda un enlace al correo, no lo abras en tu ordenador: pégalo aquí.',
      pegarEnlace: 'Pega aquí el enlace del correo',
      abrirEnlace: 'Abrir aquí',
      escribir: 'Escribe aquí (o haz clic en la página y teclea)',
      enviar: 'Enviar',
      pantalla: 'Página de entrada de Claude',
      comprobandoIp: 'Comprobando que sale por la IP de esta cuenta…',
      abriendo: 'Abriendo la entrada de Claude…',
      comprobando: 'Comprobando la cuenta…',
      conectada: '{nombre} vuelve a estar conectada. Sus agentes ya pueden usarla.',
      cancelada: 'Cancelado. No se ha tocado nada.',
      noSePudo: 'No se ha podido abrir la reconexión.',
      sinRespuesta: 'La máquina de esta cuenta no responde. Vuelve a intentarlo en un rato.',
      barra: 'claude.ai',
      reintentar: 'Volver a intentar',
    },
    tiempo: {
      enMin: 'en {m} min',
      enH: 'en {h} h',
      enHMin: 'en {h} h {m} min',
      manana: 'mañana',
      dias: 'dom,lun,mar,mié,jue,vie,sáb',
      ahora: 'ahora',
      haceS: 'hace {s} s',
      haceMin: 'hace {m} min',
    },
  },
  salaControl: {
    grupos: { negocio: 'Negocio', calypso: 'Calypso', coste: 'Coste', obras: 'Obras' },
    titulo: 'Sala de Control',
    subtitulo: 'Cómo va Calypso, ahora mismo · mismos números que el CRM',
    abrir: 'Entrar en la Sala de Control',
    dias: '{n} días',
    cerrar: 'Cerrar la Sala de Control',
    alDia: 'al día · {hora}',
    conRetraso: 'datos con retraso',
    sinDatos: 'sin datos todavía',
    cargando: 'Cargando los números…',
    errorCarga: 'No se han podido cargar los números. Se reintenta solo.',
    pestanas: { pulso: 'Pulso', dinero: 'Dinero', fans: 'Fans', seguridad: 'Seguridad', maquina: 'Máquina', cuentas: 'Cuentas y lupa', cerebros: 'Cerebros', gasto: 'Gasto', obra: 'Obra', taller: 'Taller' },
    gasto: {
      periodo: 'Periodo',
      periodos: { hoy: 'Hoy', ayer: 'Ayer', '7': '7 D', '30': '30 D', mes: 'Este mes', fechas: 'Elegir fechas' },
      desde: 'Desde',
      hasta: 'hasta',
      mesesCortos: 'ene feb mar abr may jun jul ago sep oct nov dic',
      mesesLargos: 'enero febrero marzo abril mayo junio julio agosto septiembre octubre noviembre diciembre',
      fecha: '{dia} {mes}',
      rango: '{desde} – {hasta}',
      rangoMismoMes: '{desde}–{hasta} {mes}',
      etiquetaHoy: 'Hoy, {fecha}',
      etiquetaAyer: 'Ayer, {fecha}',
      tramo: '{desde}–{hasta} UTC',
      diaCompleto: 'día completo',
      diaDesde: 'desde las {hora} UTC',
      diasConDatos: '{n} de {m} días con datos',
      titularAria: 'Rentabilidad del periodo',
      frase: 'Servir Calypso se come el',
      deNuestraParte: 'de nuestra parte',
      barraAria: '{pct} frente a un objetivo de menos del 20 %',
      barraObjetivo: 'objetivo < 20 %',
      punto: '1 punto',
      puntos: '{n} puntos',
      porEncima: 'por encima del objetivo',
      porDebajo: 'por debajo del objetivo',
      enObjetivo: 'Justo en el objetivo',
      ayerFue: 'ayer {pct}',
      anteayerFue: 'anteayer {pct}',
      diaAnteriorFue: 'el día anterior {pct}',
      mejorDia: 'el mejor día, {fecha}, {pct}',
      conEstimado: '{texto} (estimado)',
      enMes: 'En {mes}: {pct} · {usd}',
      gastado: 'Gastado',
      gastadoTramo: 'de {desde} a {hasta} UTC',
      ayerAEstaHora: 'ayer a esta hora: {usd}',
      sinApuntesHoy: 'todavía no se ha subido ningún apunte de hoy',
      estimadoConConsumo: 'estimado con el consumo',
      enParteEstimado: 'en parte estimado con el consumo',
      registrado: 'registrado llamada a llamada',
      nuestraParte: 'Nuestra parte',
      nuestraParteAyuda: 'Nuestra parte: el 10 % del neto que vende Calypso (lo que queda tras la comisión de OnlyFans; PPV y propinas de la IA, como en la pestaña Dinero).',
      deNetos: 'de {usd} netos',
      nosQueda: 'Nos queda',
      nosQuedaDetalle: 'nuestra parte menos el gasto',
      notaHoyDesde: 'Hoy se cuenta desde las {hora} UTC: antes de esa hora no hay registro.',
      notaCompensado: 'Ya restado del gasto: {usd} de {motivo}. Lo que se gaste con ese crédito sigue contando como gasto.',
      compensadoServicio: 'Por servicio es lo que costó cada uno; del total ya se restan {usd} de {motivo}.',
      notaFacturaManda: 'OpenRouter{dias} cuenta lo que facturó ({facturado}); Calypso había apuntado {apuntado}.',
      notaFacturaMandaCortadas: 'OpenRouter{dias} cuenta lo que facturó ({facturado}); Calypso había apuntado {apuntado}. La diferencia son sobre todo respuestas cortadas, que se cobran enteras.',
      notaFacturaDias: ' (en {n} días del periodo)',
      notaEstimadoAntes: 'De 00:00 a {hora} UTC es estimado (antes de que Calypso apuntara cada llamada); desde entonces, apuntado.',
      notaHorasEstimadas: 'Rayado: horas estimadas con el consumo y la tarifa de cada servicio (antes de que Calypso apuntara cada llamada).',
      notaDiaHorasEstimadas: '{dia}: estimado con el consumo y la tarifa, hora a hora; reparto aproximado y sin detalle por cuenta o servicio.',
      cuadreOk: 'Comprobado con lo cobrado: {proveedor} ±{n} %.',
      cuentaN: '{proveedor} (cuenta {n})',
      cuadreMenor: 'Lo apuntado en {proveedor} es un {n} % menor que lo que nos cobró: esa cuenta la comparten otros usos.',
      verDetalle: 'Ver detalle',
      nivel: { decision: 'Te necesita', aviso: 'Aviso' },
      porHora: 'Gasto por hora',
      porHoraSub: 'en dólares · UTC',
      porHoraAria: 'Gasto por hora, en dólares',
      sinRegistro: 'sin registro',
      enCurso: 'en curso',
      sinDatosCorto: 'sin datos',
      sinDatosTramo: 'sin datos · {desde} – {hasta}',
      sinApuntesGrafica: 'Todavía no se ha subido ningún apunte de hoy.',
      sinHoras: 'Este día no tiene detalle por hora.',
      porDia: 'Cuánto se come cada día',
      porDiaSub: '% de nuestra parte',
      porDiaAria: 'Porcentaje de nuestra parte que se come el gasto, por día',
      frenteSemana: '{dia} frente a la semana',
      ayerTitulo: 'Ayer',
      notaEstimados: '{dias}: estimados con el consumo y la tarifa de cada servicio; reparto aproximado y sin detalle por cuenta o servicio.',
      notaDiaEstimado: '{dia} sin detalle por hora: estimado con el consumo y la tarifa. Las horas salen desde el {inicio}.',
      tipEstimado: 'estimado',
      tipEnCurso: 'en curso: el día no ha acabado',
      tipSinDatos: 'sin datos',
      enQueSeVa: 'En qué se va',
      repartoAproximado: 'reparto aproximado',
      grupos: {
        conversacion: 'Conversación',
        repeticion: 'Repetir respuestas que fallaron',
        revisor: 'Revisor de seguridad',
        memoria: 'Memoria y mensajes automáticos',
        otros: 'Fotos y otros',
      },
      cuentas: 'Cuentas que pierden dinero',
      cuentasHoy: 'Con el día a medias no se calcula: mira «Ayer» o «7 D»',
      cuentasSinRegistro: 'Sin detalle por cuenta antes del {fecha}',
      cuentasNinguna: 'Ninguna cuenta costó más de lo que deja',
      cuentasResumen: '{n} cuentas costaron más de lo que dejan · −{usd}',
      cuentasResumenUna: '1 cuenta costó más de lo que deja · −{usd}',
      cuentasPeores: 'Las que más pierden',
      cuenta: 'Cuenta',
      nosDeja: 'Nos deja',
      nosCuesta: 'Nos cuesta',
      detalle: 'Detalle técnico',
      detalleSubDia: 'servicios y lo que nos han cobrado',
      detalleSubDias: 'día a día y de dónde sale cada cifra',
      detalleSubEstimado: 'de dónde sale cada cifra',
      porServicio: 'Por servicio',
      llamadas: '{n} llamadas',
      servicios: {
        deepseek: 'DeepSeek directo',
        deepseekOr: 'DeepSeek vía OpenRouter',
        claude: 'Claude (respaldo)',
        qwen: 'Qwen (lee fotos)',
        gemini: 'Gemini',
        otros: 'Otros servicios',
      },
      precio: {
        proveedor: 'precio de la factura',
        tarifa: 'precio de tarifa',
        estimado: 'estimado (llamadas cortadas)',
        sin_tarifa: 'sin tarifa: no suma',
        mixto: 'factura y tarifa',
      },
      cuadreTitulo: 'Lo apuntado frente a lo que nos han cobrado',
      apuntadoCobrado: '{apuntado} apuntados · {cobrado} cobrados',
      sinCobrado: '{apuntado} apuntados · aún sin lo cobrado',
      apuntadoFacturado: '{apuntado} apuntados · {cobrado} facturados',
      calculadoFacturado: '{apuntado} calculados (en parte estimados) · {cobrado} facturados',
      facturaOtras: 'Otras llaves de OpenRouter (agentes y pruebas): {usd} facturados aparte.',
      compartida: 'Esa cuenta la comparten otros usos.',
      cortadas: 'Llamadas que se cortaron',
      cortadasDetalle: '· ya incluidos en lo gastado',
      avisosTecnicos: 'Avisos técnicos',
      notaEstimadosDetalle: 'Los días estimados ({dias}) no tienen registro llamada a llamada: se estimaron con el consumo de cada servicio y su tarifa, y el reparto por partidas es aproximado. Por eso no tienen reparto por servicio ni llamadas cortadas, y solo OpenRouter se compara con su factura. Todo eso existe desde el {inicio} (mira «Hoy»).',
      diaADia: 'Día a día',
      dia: 'Día',
      seCome: 'Se come',
      comoSeCalculo: 'Cómo se calculó',
      total: 'Total',
      origen: {
        estimadoCerrado: 'estimado · cerrado',
        estimadoProvisional: 'estimado · provisional',
        registroCerrado: 'registrado · cerrado',
        registroProvisional: 'registrado · provisional',
        sinDatos: 'sin datos',
        enCurso: 'en curso',
      },
      provisionalNota: '«Provisional»: ese día aún no está cerrado en las cuentas y la cifra puede ajustarse un poco.',
      cargandoAria: 'Cargando el gasto',
      vacioTitulo: 'Aún no hay gasto apuntado para estas fechas',
      vacioSub: 'El gasto se apunta desde el {inicio} (hasta el {fin}, estimado con el consumo).',
      verHoy: 'Ver hoy',
      calculandoHoyTitulo: 'Calculando el gasto de hoy',
      calculandoHoySub: 'El día empieza a las 00:00 UTC; las primeras cifras salen en unos minutos.',
      notaDetalleMixto: 'Desde las {hora} UTC: el reparto por servicio y por cuenta solo cuenta desde esa hora (lo de antes es estimado y no tiene ese detalle).',
      error: 'No se ha podido cargar el gasto.',
      reintentar: 'Reintentar',
      sinRefrescar: 'No se ha podido actualizar · datos de hace {edad}',
      actualizado: 'Actualizado hace {edad}',
      registradoHasta: 'gasto registrado hasta las {hora} UTC',
    },
    rincones: {
      cargando: 'Calculando este rincón…',
      calculandoPrimera: 'Calculando por primera vez: tarda unos segundos…',
      sinDatos: 'Todavía no hay datos de este rincón.',
      sinDatosPorError: 'No se han podido leer los datos: {motivo}.',
      desfasado: 'Estos datos son de hace {edad}: {motivo}. Se reintenta solo.',
      edad: 'Datos de hace {edad}',
      errores: {
        sin_credenciales: 'faltan las llaves del almacén de datos',
        tiempo_agotado: 'el almacén de datos tardó demasiado',
        sin_memoria: 'la consulta es demasiado grande para el almacén de datos',
        cuota_agotada: 'se ha gastado el cupo del almacén de datos',
        fuente_cambiada: 'el CRM ha cambiado sus datos y hay que adaptar la sala',
        error: 'no se han podido actualizar',
      },
      dinero: {
        ventasHoy: 'Ventas netas hoy',
        ppv: 'De PPV',
        propinas: 'De propinas',
        compras: '{n} PPV comprados',
        enChats: 'Todo lo gastado en chats de Calypso',
        enChatsAyuda: 'Lo que gastan los fans en conversaciones que lleva Calypso, aunque el PPV lo mandara una persona o un masivo',
        porHora: 'Ventas netas por hora',
        porHoraLeyendaHoy: 'Hoy',
        porHoraLeyendaAyer: 'Ayer',
        precios: 'Unlock rate por precio',
        preciosSub: 'Últimos 7 días',
        internas: 'Fuera de estas cifras, como en el CRM: {ventas} de {n} cuentas internas (agencia de la casa).',
      },
      fans: {
        chatean: 'Fans que chatean hoy',
        pagan: 'Fans que pagan hoy',
        embudo: '¿Cómo llega un fan a comprar?',
        embudoSub: 'Últimos 7 días',
        atendidos: 'Fans a los que Calypso contesta',
        conPpv: 'Reciben al menos un PPV',
        compran: 'Compran al menos uno',
        repiten: 'Vuelven a comprar',
        deLosQueReciben: '{p} de los que reciben',
        deLosQueCompran: '{p} de los que compran',
        semana: 'Día a día · últimos 7 días',
        semanaSub: 'Días cerrados; hoy va rayado porque está a medias',
        chateanDia: 'Fans que chatean cada día',
        paganDia: 'Fans que pagan cada día',
        minPpv: 'el primer PPV llega a los {min} min',
        minCompra: 'y la compra, {min} min después',
      },
      seguridad: {
        escalaciones: '¿Por qué pasamos fans a un humano?',
        escalacionesSub: 'Hasta esta hora',
        clases: { crisis: 'Crisis (el fan está mal)', amenaza_creadora: 'Amenaza a la creadora', menor: 'Menor de edad', otro: 'Otras' },
        total: 'Total',
        hoy: 'Hoy',
        ayer: 'Ayer',
        sem: 'Semana pasada',
        frenos: '¿Qué se ha parado antes de llegar al fan?',
        frenosSub: 'Hoy',
        tipos: {
          rewrite_performed: 'Mensajes reescritos',
          freno_candados: 'Ventas frenadas por los candados',
          of_gate_hit: 'Temas prohibidos de OnlyFans',
          safety_block: 'Mensajes bloqueados',
          fan_blocklist: 'Fans cortados por seguridad',
          crisis_candado_venta_por_fan: 'Ventas paradas por una crisis',
          refusal_detected: 'La IA se negó a contestar',
        },
        desconocidas: 'Hay tipos de escalación nuevos que la sala aún no nombra (van en «Otras»): {lista}.',
      },
      maquina: {
        p95: 'El 5 % más lento tarda',
        objetivo: 'objetivo 30 s',
        p50: 'Respuesta típica',
        respaldo: 'Saltó el modelo de respaldo',
        coste: 'Coste de tareas de apoyo hoy',
        costeAyuda: 'Sin contar el modelo que chatea',
        porHora: '¿Quién es el lento?',
        porHoraSub: 'El 5 % más lento de las respuestas, hora a hora, hoy',
        tabla: 'Por modelo, últimas 24 h',
        respuestas: 'Respuestas',
        tipica: 'Típica',
        lento: '5 % lento',
        modelos: { 'deepseek-v4-pro': 'Modelo normal', 'deepseek-v4-pro-ckpt-abril': 'Brazo «abril»', 'deepseek-v4-flash': 'Modelo rápido', otro: 'Otros' },
      },
      cuentas: {
        total: 'Cuentas con Calypso',
        trabajando: 'Trabajando ahora',
        trabajandoAyuda: 'Han contestado en la última hora',
        calladas: 'Calladas de repente',
        calladasAyuda: 'Contestaban en las 3 horas anteriores y llevan una hora sin contestar',
        calladasDetalle: 'calladas · paradas',
        paradasLista: 'Cuentas paradas',
        paradasAyuda: 'Trabajaban esta semana y llevan 24 horas sin contestar nada',
        calladasLista: 'Cuentas calladas',
        experimento: 'Experimento en marcha',
        brazo: 'Brazo {b}',
        fans: 'Fans',
        respuestas: 'Respuestas 24 h',
        buscar: 'Busca una cuenta…',
        cuenta: 'Cuenta',
        ventas: 'Ventas hoy',
        unlock: 'Unlock',
        ultima: 'Última respuesta',
        modelo: 'Modelo',
        sinResultados: 'Ninguna cuenta con ese nombre.',
        masCuentas: 'y {n} más',
      },
    },
    ventasHoy: 'Ventas netas hoy',
    ventasHoyAyuda: 'Ventas netas de Calypso hoy · día UTC, desde las {desde} · hasta las {hora}',
    vsAyer: 'vs ayer a esta hora',
    vsSemana: 'vs el mismo día de la semana pasada',
    ayer: 'ayer',
    semana: 'semana',
    semanaCorta: 'sem',
    verTodas: 'Ver las {n}',
    verMenos: 'Ver menos',
    igual: '=',
    pts: '{valor} pts',
    acumulado: 'Ventas netas acumuladas del día',
    hoy: 'Hoy',
    ayerLeyenda: 'Ayer',
    semanaLeyenda: 'Semana pasada',
    grafica: {
      franjaHora: '{desde} – {hasta} UTC',
      delGasto: 'Del gasto del periodo',
      ayerAEstaHora: 'Ayer a esta hora',
      semanaAEstaHora: 'Semana pasada a esta hora',
      frenteAyer: 'frente a ayer',
      hastaHora: 'Hasta las {hora} UTC',
      gasto: 'Gasto',
      delDia: 'Del total del día',
      seCome: 'Se come',
      gastado: 'Gastado',
      nuestraParte: 'Nuestra parte',
      estimado: 'Estimado con el consumo y la tarifa',
      enCurso: 'La hora que se está apuntando ahora',
      media7: 'Media de 7 días',
      normal: 'Lo normal',
      raroAlto: 'Por encima de lo normal',
      raroBajo: 'Por debajo de lo normal',
      hoyAMedias: 'Hoy, a medias',
      hoyAMediasPie: 'El día no ha acabado: no lo compares con días enteros',
      abrirDia: 'Clic para ver ese día',
      anterior: 'Periodo anterior',
      objetivo: 'Objetivo',
      semanaDe: 'Semana del {dia}',
      incompleta: 'Semana sin acabar',
      sinDato: 'Sin dato',
    },
    porHora: {
      titulo: 'Hora a hora, hoy frente a ayer',
      ventas: 'Ventas por hora',
      unlock: 'Unlock rate del día',
      pagan: 'Fans nuevos que pagan',
      reply: 'Reply time del día',
      cvr: 'Fan conversion del día',
      porFan: '$ por fan del día',
      porMsg: '$ por mensaje del día',
      ultima: 'última hora cerrada',
      leyendaPie: 'horas UTC · ventas y fans que pagan, de cada hora; el resto, del día hasta esa hora (como las tarjetas)',
    },
    tabla: {
      titulo: 'Cuentas',
      sub: 'Las {n} que más venden hoy · pulsa una columna para ordenar',
      cuenta: 'Cuenta',
      ventas: 'Ventas',
      porMsg: '$/mensaje',
      porFan: '$/fan',
      unlock: 'Unlock',
      golden: 'Golden',
      fans: 'Fans',
      cvr: 'Conversión',
      reply: 'Respuesta',
      estado: 'Estado',
      callada: 'callada',
      parada: 'parada',
      cargando: 'Cargando las cuentas…',
      sinDatos: 'Todavía no hay cuentas con ventas hoy.',
    },
    sinAvisos: 'Sin avisos abiertos',
    diaEntero: { largo: 'ayer, día entero: {valor}', corto: 'ayer entero {valor}', ayuda: 'La cifra grande es la de hoy hasta ahora, con el día a medias. Esta es la de ayer con el día entero.' },
    modelos: { sinComparar: 'sin comparación: el periodo anterior aún no está guardado entero', vsInicio: '{b} frente a {a} (del mismo modelo)', ventasPeriodo: 'Ventas netas · {modelo} · {periodo}', vsAnterior: 'vs el mismo modelo en {periodo}', anteriorCorto: 'anterior', fansPeriodo: '{fans} fans distintos · el {pct} % de los fans', faltan: 'Aún se están guardando días de este periodo (faltan {n}): las cifras pueden quedarse cortas.', horasDia: 'Hora a hora, este día frente al anterior', ventasDia: 'Ventas netas por día', familias: { 'deepseek-v4-pro': 'DeepSeek Pro', 'deepseek-v41-flash': 'DeepSeek Flash', 'deepseek-v4-flash': 'DeepSeek Flash' }, titulo: 'Ver por modelo', todos: 'Todos', ventasDe: 'Ventas netas hoy · {modelo}', vsAyer: 'vs el mismo modelo ayer a esta hora', fans: '{fans} fans hoy · el {pct} % de los fans', nota: 'Por modelo, las mismas cuentas del CRM repartidas por el modelo que escribió cada conversación (dinero al ~98 %). El tiempo de respuesta no se puede separar por modelo.' },
    parado: { hoy: 'Hoy', ayer: 'Ayer', valor: 'Calypso parado', nota: 'ojo: ayer Calypso estuvo parado ({tramos} UTC)', ayuda: 'Ayer Calypso apenas contestó en esas horas: desde entonces, la comparación con ayer sale mejor de lo que es.' },
    retrasoAviso: 'Algunos datos llegan con retraso: lo atenuado no está al día.',
    pantalla: { ventas: 'Calypso · {hora} UTC', vsAyer: '{delta} vs ayer' },
    obra: {
      titulo: 'Épica 005 de Calypso',
      sub: 'Leído del código de Calypso hace {edad} · nada escrito a mano',
      cuando: '{dia}, {hora} de Tailandia',
      hoy: 'hoy',
      sinDatos: 'Todavía no se ha leído el estado de la obra.',
      salud: { verde: 'En marcha', ambar: 'En marcha, con un aviso', rojo: 'Parada' },
      saludDetalle: {
        verde: 'Todo lo empezado tiene a alguien trabajando.',
        ambar: '{n} piezas empezadas y ahora mismo nadie trabaja en ellas.',
        ambarUno: '1 pieza empezada y ahora mismo nadie trabaja en ella.',
        rojo: 'Nada se ha movido en {dias} días.',
        rojoNunca: 'Todavía no se ha movido nada.',
      },
      cerradas24h: '{n} cerradas en las últimas 24 h.',
      cerradas24hUno: '1 cerrada en las últimas 24 h.',
      pctEpica: 'de toda la épica',
      piezasDe: '{hechas} de {total} piezas',
      ruta: 'Hoja de ruta',
      rutaSub: 'cada etapa empieza cuando la anterior deja listas sus piezas clave',
      estamosAqui: 'estamos aquí',
      etapaN: 'Etapa {n}',
      etapaCuenta: '{hechas} de {total}',
      avance: 'Avance día a día · etapa {n}',
      avanceSub: 'piezas cerradas, acumuladas',
      meta: 'meta: {n}',
      prevista: 'Fecha prevista de la etapa: {fecha}',
      sinPrevista: 'Fecha prevista: sale sola con {n} días de datos',
      tablero: 'Tablero de la etapa {n}',
      porHacer: 'Por hacer',
      enCurso: 'En curso',
      hecho: 'Hecho',
      yMas: 'y {n} más',
      verMenos: 'ver menos',
      parada: 'parada · nadie trabaja en ella',
      lleva: 'lo lleva «{tema}»',
      toca: 'Te toca a ti',
      tocaSub: 'lo único que frena la obra por tu lado',
      allowNada: 'Subidas esperando tu Allow: ninguna',
      allowN: 'Subidas esperando tu Allow: {n}',
      preguntas: 'Preguntas de la épica sin contestar: {n}',
      preguntasNada: 'Ninguna pregunta de la épica sin contestar',
      frentes: 'Frentes de trabajo',
      frentesSub: 'toda la épica',
      frentesQuietos: 'y {n} frentes sin empezar',
      riesgos: 'Riesgos',
      riesgosSub: 'se detectan solos',
      sinRiesgos: 'Ninguno ahora.',
      riesgo: {
        quieta: 'Nada se ha movido en {dias} días.',
        paradas: '{n} piezas empezadas sin nadie trabajando en ellas.',
        paradasUno: '1 pieza empezada sin nadie trabajando en ella.',
        allow: '{n} subidas esperan tu Allow.',
        allowUno: '1 subida espera tu Allow.',
        sinEmpezarEtapa: 'La etapa {etapa} aún no ha empezado.',
      },
      ultimo: 'Lo último',
      hecha: 'Hecha',
      subida: 'Subida a producción',
      errores: {
        sin_github: 'no se pudo traer lo último de Calypso',
        tablero_roto: 'falló el tablero de la obra de Calypso',
        formato: 'el tablero de la obra de Calypso cambió de forma',
        error: 'no se pudo leer la obra',
      },
    },
    enMarcha: {
      titulo: 'En marcha ahora',
      sub: 'subidas y entregas, al momento · se actualiza cada 10 s',
      nada: 'Ahora mismo no se está subiendo ni entregando nada.',
      ultimaProd: 'Última subida a producción: {cuando} ({como}).',
      desde: 'hace {edad}',
      empezo: 'empezó hace {edad}',
      teToca: 'te toca: dale al Allow',
      recientes: 'Últimas subidas (24 h)',
      destino: { produccion: 'Producción', pruebas: 'Pruebas', entrega: 'Entrega' },
      estados: { pendiente: 'pendiente', en_curso: 'en marcha', esperando_tu: 'esperando tu Allow', hecho: 'hecho', fallo: 'falló', saltado: 'no hacía falta' },
      resultado: { hecho: 'Hecha', fallo: 'Falló', cancelado: 'Cancelada', en_curso: 'En marcha', esperando_tu: 'Esperando tu Allow' },
      fases: {
        prepara: 'Prepara',
        comprueba: 'Pruebas automáticas',
        allow: 'Tu Allow',
        sube: 'Sube a los servidores',
        canario: 'Prueba con unos pocos',
        cierra: 'Termina',
        sube_pruebas: 'Sube a pruebas',
      },
      entrega: { comprueba: 'Comprueba', revisa: 'La revisa otro agente', prepara: 'Prepara la entrada', cola: 'En la cola', dentro: 'Dentro de la oficial' },
    },
    taller: {
      titulo: 'Taller: agentes, líneas de trabajo y versión oficial',
      sub: 'Actualizado hace {edad} · se actualiza solo cada minuto',
      actualizarYa: 'Actualizar ya',
      actualizando: 'Actualizando…',
      sinDatos: 'Todavía no se ha leído el taller.',
      salud: { verde: 'Todo fluye', ambar: 'Hay cosas que mirar', rojo: 'Algo está atascado' },
      saludVerde: 'Nada atascado ni abandonado.',
      cifraAgentes: 'agentes en la oficina',
      cifraLineas: 'líneas de trabajo en GitHub',
      cifraAbandonadas: 'abandonadas',
      cifraEntraron: 'cambios entraron hoy (24 h)',
      donutTitulo: 'Líneas de trabajo en GitHub (branches)',
      donutAria: '{n} líneas de trabajo en GitHub, por quién las lleva',
      donutCentro: 'en GitHub',
      lineas: { conAgente: 'De un agente trabajando', conPropuesta: 'Esperando entrar', abandonadas: 'Abandonadas (sin agente)', vacias: 'Vacías (ya están en la oficial)' },
      mirar: 'Lo que necesita tu atención',
      nadaQueMirar: 'Nada: todo sigue su camino.',
      aviso: {
        atascado1: '«{titulo}» está atascado y no avanza solo.',
        atascado: '{n} agentes atascados: no avanzan solos.',
        te_espera1: '«{titulo}» espera tu respuesta.',
        te_espera: '{n} agentes esperan tu respuesta.',
        quieto1: '«{titulo}» lleva más de un día sin nada que hacer: ciérralo o dale trabajo.',
        quieto: '{n} agentes llevan más de un día sin nada que hacer: ciérralos o dales trabajo.',
        sin_nadie1: '«{titulo}» se quedó en GitHub sin ningún agente.',
        sin_nadie: '{n} líneas de trabajo se quedaron en GitHub sin ningún agente.',
        demasiados: 'Demasiadas líneas abandonadas en GitHub ({n}). Si alguna vale, pide a un agente que la retome; el resto se guarda y se borra sola a los 14 días.',
        sin_github: 'GitHub no responde: lo de los agentes está al día, lo de GitHub no.',
      },
      oficial: 'Versión oficial ({nombre})',
      pruebas: { verde: 'pruebas en verde', rojo: 'pruebas en rojo', en_curso: 'pruebas en marcha', sin_datos: 'sin pruebas' },
      ultimoCambio: 'Último cambio hace {edad}: {titulo}',
      verEnGitHub: 'Ver en GitHub',
      verLineas: 'Verlas en GitHub',
      agentesTitulo: 'Agentes en la oficina ({n})',
      agentesQue: 'Cada uno trabaja en su propia copia en nuestros servidores (lo que se llama worktree). Toca uno para abrir su chat.',
      sinAgentes: 'Ningún agente trabajando en esta sala ahora mismo.',
      yaEnGitHub: 'Su línea de trabajo ya está en GitHub',
      soloEnSuCopia: 'Aún solo en su copia: llega a GitHub cuando lo entrega',
      activo: 'activo hace {edad}',
      lineasTitulo: 'Líneas de trabajo en GitHub ({n})',
      lineasQue: 'Lo que GitHub llama branches: cada una es un cambio que aún no está en la versión oficial.',
      olvidados: 'Abandonadas ({n})',
      flujo: 'Cómo entra un cambio en la versión oficial',
      flujoSub: 'cuántos hay en cada paso ahora mismo',
      pasoN: 'Paso {n}',
      flujoPasos: { haciendo: 'Trabaja en su copia', revisando: 'Lo revisa otro agente', entrando: 'Espera su turno', entraron: 'Entró (24 h)' },
      flujoQue: {
        haciendo: 'El agente cambia cosas en su propia copia; aún no toca la versión oficial.',
        revisando: 'Otro agente comprueba que está bien antes de dejarlo entrar.',
        entrando: 'Aprobado: espera en la cola (pull request) para entrar en master.',
        entraron: 'Ya forma parte de la versión oficial.',
      },
      fuera: 'Agentes parados (de {n})',
      grupos: { atascado: 'Atascados', te_espera: 'Te esperan a ti', en_camino: 'En camino', haciendo: 'Trabajando', quieto: 'En pausa', sin_nadie: 'Abandonadas' },
      gruposQue: {
        atascado: 'Algo falla y no avanzan solos.',
        te_espera: 'Necesitan tu respuesta para seguir.',
        en_camino: 'En revisión o esperando entrar.',
        haciendo: 'Trabajando.',
        quieto: 'Terminaron y esperan que les digas algo.',
        sin_nadie: 'Sin ningún agente.',
      },
      pasos: { atascado: 'Atascado', te_espera: 'Te espera', revisando: 'En revisión', entrando: 'Esperando entrar', haciendo: 'Trabajando', quieto: 'En pausa', sin_nadie: 'Abandonada' },
      motivos: { no_pasa: 'no pasa las pruebas', cierre_atascado: 'se atascó al entregar', fallo_agente: 'su agente tiene un fallo' },
      dejo: 'la dejó «{tema}»',
      deNadie: 'no la abrió ningún agente de la oficina',
      movido: 'tocada hace {edad}',
      seRecoge: 'se borra sola el {fecha}',
      abrirChat: 'Abrir el chat de «{tema}»',
      verPropuesta: 'Ver en GitHub',
      ritmo: 'Cambios que entraron en la versión oficial, cada día',
      ritmoSub: 'últimos 14 días · media {media} al día',
      ritmoAria: 'Cambios que entraron en la versión oficial cada día de los últimos 14, media {media} al día',
      media: 'media',
      olvido: 'Abandonadas: cuánto llevan sin tocarse',
      olvidoSub: '{n} líneas de trabajo sin agente',
      tramos: { menos3: 'menos de 3 días', de3a7: '3 a 7 días', de7a14: '7 a 14 días', mas14: 'más de 14 días' },
      olvidoNota: 'A los 14 días sin tocarse, la limpieza las guarda y las borra solas: no se pierde nada.',
      entraron: 'Lo que entró en la versión oficial (últimas 24 h)',
      entraronNada: 'Nada en las últimas 24 horas.',
      yMas: 'y {n} más',
      verMenos: 'ver menos',
      errores: { error: 'no se pudo leer el taller' },
    },
    cerebros: {
      pantalla: 'Cerebros',
      titulo: 'Qué reciben los fans ahora',
      sub: '{fans} fans en la última media hora · datos de hace {edad}',
      normal: 'Lo normal',
      normalSub: 'lo que recibe todo fan que no está en una prueba',
      version: 'Versión',
      versionHoy: 'la de hoy',
      cerebro: 'Cerebro que contesta',
      barra: 'Reparto de los fans por cerebro',
      cerebroEdad: '{fans} fans con respuesta en los últimos {min} min · datos de hace {edad}',
      deRespaldo: '(de respaldo)',
      respaldo: 'El {pct} lo contestó un cerebro de respaldo porque falló el que tocaba.',
      yaParaTodos: 'Ya para todos',
      paraTodos: 'desde el {fecha} UTC',
      vivas: 'Pruebas vivas · {n}',
      sinLados: 'Calypso no está apuntando en qué lado de cada prueba está cada fan: no se puede saber qué pruebas están vivas. Hay que revisarlo en Calypso.',
      ninguna: 'Ahora mismo no hay ninguna prueba viva: todos los fans reciben lo normal.',
      sinDatos: 'Aún no hay fans que contar.',
      barraPrueba: 'Reparto de los fans de «{nombre}»',
      loNormal: 'lo normal',
      soloA: 'Solo entre {quienes}.',
      llevaHoras: 'versión de hace {n} h',
      llevaDia: 'versión de hace 1 día',
      llevaDias: 'versión de hace {n} días',
      allow: 'Última versión: el {fecha} UTC, con tu Allow «{titulo}»',
      sinAllow: 'No está en tus Allow desde el {fecha} UTC: se encendió antes o sin cambiar ajustes',
      sinAllowNunca: 'No consta qué subida la encendió',
      sinNombre: 'sin nombre en el registro de Calypso',
      busca: 'Busca: {texto}',
      numeros: 'En las últimas 24 h de sus fans (su última versión es de antes). Con pocos fans las diferencias pueden ser azar: la de venta solo se marca firme si es muy clara y hay al menos 1.000 fans por lado.',
      numerosVersion: 'Desde su última versión (el {fecha} UTC, hace {h} h): lo de antes no se mezcla. Con pocos fans las diferencias pueden ser azar: la de venta solo se marca firme si es muy clara y hay al menos 1.000 fans por lado.',
      numerosEn: 'Versión nueva: los números salen cuando lleve una hora (dentro de {n} min); antes no dirían nada.',
      fila: {
        coste_mil: 'Coste por 1.000 respuestas',
        cache_pct: 'Caché reutilizado',
        venta_fan: 'Lo que paga cada fan',
        bloqueos_mil: 'Bloqueos por 1.000 respuestas',
      },
      firme: 'diferencia firme',
      noFirme: 'aún puede ser azar',
      puntos: '{signo}{n} puntos',
      nota: 'Una prueba sale aquí mientras sus fans estén repartidos. Cuando todos quedan en un lado, desaparece sola.',
    },
    fuentes: {
      crm: 'Datos del CRM: hace {edad}',
      conversaciones: 'Conversaciones de Calypso: hace {edad}',
    },
    metricas: {
      unlock: { nombre: 'Unlock rate', ayuda: 'PPV comprados de cada 100 enviados' },
      cvr: { nombre: 'Fan conversion', ayuda: 'Fans que pagan de cada 100 que chatean' },
      golden: { nombre: 'Golden ratio', ayuda: 'De cada 100 mensajes de Calypso, cuántos son PPV' },
      reply: { nombre: 'Reply time', ayuda: 'Tiempo medio entre el mensaje del fan y la respuesta de Calypso' },
      por_fan: { nombre: '$ por fan', ayuda: 'Ventas netas ÷ fans que chatean' },
      por_msg: { nombre: '$ por mensaje', ayuda: 'Ventas netas ÷ mensajes de Calypso' },
    },
    cache: {
      titulo: 'Caché del modelo · conversaciones',
      sub: 'De cada 100 tokens que Calypso manda al modelo, cuántos salen del caché (se cobran mucho más baratos)',
      vsAnterior: 'frente al periodo anterior ({valor})',
      objetivo: 'Objetivo: {n} %',
      faltan: 'Faltan {n} puntos para el objetivo del {objetivo} %',
      porDia: 'Por día · discontinua: objetivo del {n} %',
      error: 'No se ha podido leer el caché. Se reintenta al volver a la pestaña.',
      rellenando: 'Aún se están guardando {n} días de estas fechas o de las de comparación: el porcentaje puede cambiar (el relleno completo tarda hasta 2 h tras ponerlo en marcha).',
      aparte: 'Pokes y masivos (otro motor): {valor}',
    },
    fechas: {
      atajos: { hoy: 'Hoy', ayer: 'Ayer', '7': '7 D', '30': '30 D', mes: 'Este mes', mes_pasado: 'Mes pasado', '90': '3 M', '180': '6 M', todo: 'Todo', fechas: 'Fechas' },
      atajosLargos: { hoy: 'Hoy', ayer: 'Ayer', '7': 'Últimos 7 días', '30': 'Últimos 30 días', mes: 'Este mes', mes_pasado: 'Mes pasado', '90': 'Últimos 90 días', '180': 'Últimos 6 meses', todo: 'Todo, desde el 10 abr', fechas: 'Fechas' },
      anterior: 'Periodo anterior',
      siguiente: 'Periodo siguiente',
      abrir: 'Elegir fechas',
      rotuloHoy: 'Hoy, {fecha}',
      hastaHora: 'hasta {hora} UTC',
      nDias: '{n} días',
      irA: 'Ir a',
      elegirInicio: 'Elige el primer día',
      elegirFin: 'Elige el último día',
      aplicar: 'Ver',
      cancelar: 'Cancelar',
      pie: 'Días UTC (empiezan a las 07:00 de Tailandia). Hay datos desde el 10 abr. Los rangos son de días cerrados: hoy se ve en «Hoy».',
      diaRaro: 'Día raro',
      comparado: {
        hoy: 'Comparado con ayer y el {semana} pasado, a la misma hora',
        dia: 'Comparado con el día anterior y el mismo día de la semana pasada',
        mes_anterior: 'Comparado con {mes}, por media diaria',
        mes_anteriorIgual: 'Comparado con {mes}',
        inicio_final: 'Comparado: primer mes del rango ({a}) frente al último ({b})',
        anterior: 'Comparado con los {n} días anteriores ({a})',
        ninguna: 'Sin comparación: antes de este periodo no hay datos',
      },
      sinInternas: 'No se ha podido leer qué cuentas son de la casa: estas cifras pueden incluirlas y no cuadrar con el CRM.',
      hoyFuera: 'Hoy no entra: va en curso. Míralo en «Hoy».',
      cargando: 'Calculando el periodo…',
      error: 'No se ha podido calcular el periodo. Se reintenta al volver a elegirlo.',
      rellenando: 'Aún se están guardando {n} días de este periodo: las cifras pueden cambiar en unos minutos.',
    },
    esperado: {
      leyenda: 'Esperado',
      titulo: 'Esperado hoy a este ritmo',
      horquilla: 'entre {bajo} y {alto}',
      horquillaAyuda: 'Ganancias esperadas de hoy a este ritmo: entre {bajo} y {alto}, según cómo acabaron los últimos días',
    },
    lectura: {
      tituloRango: 'Lectura del periodo',
      cambioItem: '{nombre} {de} → {a}',
      ventas: {
        dia: 'Calypso vendió {total} ese día{cambio}.',
        rango: 'Calypso vendió {total}: {media} al día de media{cambio}.',
        cambioAnterior: ', {pct} que en el periodo anterior',
        cambioMes: ', {pct} al día que en el mes anterior',
        cambioInicioFinal: '; el último mes, {pct} al día que el primero',
        cambioDia: ', {pct} que el día anterior',
        mas: 'un {n} % más',
        menos: 'un {n} % menos',
        igual: 'casi igual',
      },
      fans: 'Chatearon {distintos} fans distintos ({media} al día de media){cambio}.',
      fansDia: 'Chatearon {fans} fans{cambio}.',
      fansDiaCambio: ', {pct} que el día anterior',
      fansCambio: '; {pct} que en el periodo con el que se compara ({a} → {b})',
      volumen: 'Crecemos por volumen, no por eficiencia: $ por fan de {a} a {b}.',
      mejoran: 'Mejoran: {lista}.',
      empeoran: 'Empeoran: {lista}.',
      momentos: '{n} momentos raros; el más fuerte, {cuando}: {claves}.',
      momento: '1 momento raro, {cuando}: {claves}.',
      sinMomentos: 'Ningún día se salió de lo normal.',
      faltan: 'Faltan {n} días por guardar: la lectura puede cambiar.',
    },
    series: { ventas: 'Ventas netas', fans: 'Fans que chatean', msgs: 'Mensajes de Calypso', unlock: 'Unlock rate', cvr: 'Fan conversion', golden: 'Golden ratio', reply: 'Reply time', por_msg: '$ por mensaje' },
    rango: {
      ventasDe: 'Ventas netas · {periodo}',
      mediaDia: 'De media, {media} al día.',
      inicioFinal: 'Primer mes: {a} al día · último mes: {b} al día',
      vsComparado: '{cambio} al día que {periodo} ({valor} $/día)',
      porDiaNota: 'Los periodos no tienen los mismos días: se compara la media por día.',
      tarjetaInicioFinal: '1.er mes → último',
      tarjetaAnterior: 'antes',
      porFanDistinto: 'por fan distinto del periodo',
      porSemana: 'por semana',
      ventasPorDia: 'Ventas netas por día',
      ventasPorDiaSub: 'Barras: cada día · línea: media de 7 días · gris: lo normal (4 semanas antes, sin días raros) · rojo: día raro',
      lineaComparado: 'Discontinua: {periodo}',
      momentos: 'Momentos raros del periodo',
      momentosSub: 'Se detectan solos: una cifra se sale de su franja normal. Pulsa uno para ver ese día.',
      cambioNivel: 'cambio de nivel',
      paraBien: 'para bien',
      verMas: 'Ver {n} más',
      verMenos: 'Ver menos',
      sinMomentos: 'Ningún día se salió de lo normal en este periodo.',
      fansTitulo: 'Fans que chatean con Calypso',
      fansDia: 'Fans que chatean cada día',
      fansDiaSub: 'Un fan cuenta una vez por día · rojo: día raro',
      distintos: 'Fans distintos, sin repetir',
      distintosSub: 'Si un fan escribe 10 días, aquí cuenta 1 vez',
      enElPeriodo: 'En el periodo',
      sumaDias: 'sumando los días saldrían {n}',
      alDia: 'Al día, de media',
      porSemanaTitulo: 'Por semana (lunes a domingo)',
      rayadas: 'Rayadas: semanas incompletas, no las compares con las demás.',
      porMes: 'Por mes',
      porMesSub: 'Meses de distinta longitud: compara la media diaria y el $ por fan, no los totales',
      mes: 'Mes',
      incompleto: '(incompleto)',
      ventasDia: 'Ventas al día',
      porFanCol: '$ por fan distinto',
      conversionCol: 'Conversión',
      vueltas: 'Cada fan chatea de media {n} días de cada {d}.',
      cuentas: 'Cuentas',
      cuentasSub: 'Las 100 que más vendieron · pulsa una columna para ordenar',
      alDiaCol: 'Al día',
      progreso: 'Progreso',
      progresoAyuda: 'Ventas al día del periodo que se juzga frente al de referencia',
      tendencia: 'Tendencia',
      nueva: 'nueva desde el {dia}',
      unDia: 'Un día',
      horasDia: 'Ventas netas acumuladas, hora a hora',
      sinHoras: 'Este día aún no tiene sus horas guardadas.',
      loNormal: 'lo normal, {bajo} – {alto}',
      vsDiaAnterior: 'vs el día anterior',
      verDia: 'Abrir ese día',
    },
    hoyExtra: {
      normal: 'normal a esta hora',
      normalCorto: 'normal',
      dentro: 'dentro',
      fuera: 'fuera',
      fansHoy: 'Fans que han chateado hoy',
      fansHoySub: 'Cada fan cuenta una vez en el día, aunque escriba 50 mensajes',
      hastaAhora: 'Hoy, hasta las {hora}',
      ayerAEstaHora: 'ayer a esta hora: {n}',
      semanaAEstaHora: 'el {dia} pasado a esta hora: {n}',
      ultimos30: 'Fans que chatean cada día · últimos 30 días cerrados',
      distintos7: 'Últimos 7 días',
      distintos30: 'Últimos 30 días',
      sumaBarras: 'Sumando las barras de 7 días salen {suma}: cada fan chatea de media {n} días por semana. Por eso los distintos nunca son la suma de los días.',
    },
  },
} as const;

export default es;
